// src/screens/FormListScreen.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Modal,
  TouchableOpacity,
  RefreshControl,
  Alert,
  AppState,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  Appbar,
  Chip,
  ActivityIndicator,
  Text,
  ProgressBar,
  Badge,
  Divider,
  IconButton,
  Snackbar,
  Menu,
  TextInput,
} from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AVAQualisTheme } from '../theme/theme';
import { styles } from './FormListScreen.css';
import { Icon } from 'react-native-paper';
import StorageManager from '../utils/StorageManager';

import { MaterialCommunityIcons } from '@expo/vector-icons';
import EmailLeadershipModal from '../components/EmailLeadershipModal';

// Importar dados necessários
import { AVALIADORES, getUnidadeHierarchy, searchUnidades } from '../data/EstruturaOrganizacional';

import * as api from '../lib/api';
import { transformApiToForm } from '../utils/transformers';

// CONFIGURAÇÕES DE PERFORMANCE ULTRA OTIMIZADAS
const DEBOUNCE_DELAY = 1000;
const THROTTLE_DELAY = 2000;
const MAX_RETRY_ATTEMPTS = 3;
const UPDATE_THROTTLE = 5000;

// NOVA ADIÇÃO: Função de determinação de status de sincronização (copiada do ResponsesScreen)
const determineResponseSyncStatus = (localResponse, cloudResponse) => {
  // Copiar a mesma função do ResponsesScreen.js
  if (!cloudResponse) {
    return 'local-only';
  }

  if (localResponse.synced && localResponse.syncedAt && cloudResponse) {
    const localAnswers = JSON.stringify(localResponse.answers || {});
    const cloudAnswers = JSON.stringify(cloudResponse.answers || {});

    if (localAnswers === cloudAnswers) {
      return 'synced';
    }

    const localDate = new Date(localResponse.lastModified || localResponse.syncedAt);
    const cloudDate = new Date(cloudResponse.lastModified || cloudResponse.syncedAt);

    if (Math.abs(localDate - cloudDate) < 10000) {
      return 'synced';
    }
  }

  const localModified = new Date(localResponse.lastModified || localResponse.createdAt);
  const cloudModified = new Date(cloudResponse.lastModified || cloudResponse.createdAt);

  if (localModified > cloudModified) {
    return 'local-newer';
  } else if (cloudModified > localModified) {
    return 'cloud-newer';
  }

  return 'local-newer';
};

// NOVA ADIÇÃO: Classe utilitária para verificação de mudanças significativas
class ChangeDetector {
  /**
   * Verifica se houve mudanças significativas entre dois objetos de dados
   * Ignora timestamps e metadados de sincronização
   */
  static hasSignificantChanges(existingData, newData) {
    if (!existingData || !newData) {
      return true; // Se não há dados existentes, considerar como mudança
    }

    try {
      // Extrair apenas os dados relevantes para comparação
      const extractRelevantData = data => ({
        answers: data.answers || {},
        findings: data.findings || {},
        savedFindings: data.savedFindings || {},
        notApplicableReasons: data.notApplicableReasons || {},
        attachments: this.normalizeAttachments(data.attachments || {}),
        isComplete: data.isComplete || false,
      });

      const existingRelevant = extractRelevantData(existingData);
      const newRelevant = extractRelevantData(newData);

      return JSON.stringify(existingRelevant) !== JSON.stringify(newRelevant);
    } catch (error) {
      console.warn('⚠️ Erro ao verificar mudanças:', error);
      return true; // Em caso de erro, assumir que há mudanças
    }
  }

  /**
   * Normaliza anexos para comparação (conta apenas a quantidade)
   */
  static normalizeAttachments(attachments) {
    if (!attachments || typeof attachments !== 'object') {
      return {};
    }

    const normalized = {};
    Object.keys(attachments).forEach(key => {
      const attachment = attachments[key];
      if (Array.isArray(attachment)) {
        normalized[key] = attachment.length;
      } else {
        normalized[key] = 0;
      }
    });
    return normalized;
  }

  /**
   * Cria um snapshot limpo dos dados para comparação
   * Remove timestamps e dados voláteis
   */
  static createCleanSnapshot(data) {
    if (!data) return '';

    try {
      const cleanData = {
        answers: this.sortObject(data.answers || {}),
        findings: this.sortObject(data.findings || {}),
        savedFindings: this.sortObject(data.savedFindings || {}),
        notApplicableReasons: this.sortObject(data.notApplicableReasons || {}),
        attachments: this.normalizeAttachments(data.attachments || {}),
        isComplete: Boolean(data.isComplete),
      };

      return JSON.stringify(cleanData);
    } catch (error) {
      console.error('❌ Erro ao criar snapshot limpo:', error);
      return '';
    }
  }

  /**
   * Ordena objeto para comparação consistente
   */
  static sortObject(obj) {
    if (!obj || typeof obj !== 'object') return {};

    const sorted = {};
    Object.keys(obj)
      .sort()
      .forEach(key => {
        sorted[key] = obj[key];
      });
    return sorted;
  }
}

// EXTENSÃO do StorageManager com métodos otimizados
class OptimizedStorageManager extends StorageManager {
  /**
   * Verifica mudanças significativas antes de salvar
   */
  static async hasSignificantChanges(existingResponse, newData) {
    return ChangeDetector.hasSignificantChanges(existingResponse, newData);
  }

  /**
   * Salva resposta apenas se houver mudanças reais
   */
  static async saveResponseDataOptimized(responseData, isAutoSave = false, isComplete = false) {
    try {
      const existingResponse = await this.getFormResponse(responseData.formId, responseData.id);

      // Se não é auto-save e não está completo, verificar mudanças
      if (existingResponse && !isComplete) {
        const hasChanges = await this.hasSignificantChanges(existingResponse, {
          answers: responseData.answers,
          findings: responseData.findings,
          savedFindings: responseData.savedFindings,
          notApplicableReasons: responseData.notApplicableReasons,
          attachments: responseData.attachments,
        });

        if (!hasChanges && isAutoSave) {
          console.log('📋 Auto-save cancelado: Nenhuma mudança detectada');
          return true;
        }

        if (!hasChanges && !isAutoSave) {
          console.log('📋 Salvamento cancelado: Nenhuma mudança detectada');
          return true;
        }
      }

      // Preservar dados de sincronização se existirem
      const optimizedResponseData = {
        ...responseData,
        createdAt:
          existingResponse?.createdAt || responseData.createdAt || new Date().toISOString(),
        // Só atualizar lastModified se houver mudanças reais ou estiver completo
        lastModified:
          existingResponse && !isComplete
            ? (await this.hasSignificantChanges(existingResponse, responseData))
              ? new Date().toISOString()
              : existingResponse.lastModified
            : new Date().toISOString(),
        // Preservar status de sincronização
        synced: existingResponse?.synced || responseData.synced || false,
        syncedAt: existingResponse?.syncedAt || responseData.syncedAt,
        syncStatus: existingResponse?.syncStatus || responseData.syncStatus || 'local-only',
      };

      return await this.saveFormResponse(optimizedResponseData);
    } catch (error) {
      console.error('❌ Erro no salvamento otimizado:', error);
      return false;
    }
  }

  /**
   * CORREÇÃO 1: Função melhorada para calcular estatísticas de progresso
   * Agora filtra por avaliador atual corretamente
   */
  static async getFormProgressStats(formId) {
    try {
      // CORREÇÃO: Buscar todas as respostas do formulário
      const responses = await this.getResponsesByFormId(formId);

      // CORREÇÃO: Filtrar apenas respostas do avaliador atual
      const currentAvaliador = this.getDefaultAvaliador();
      const avaliadorResponses = responses.filter(
        r => r.avaliador && r.avaliador.id === currentAvaliador.id
      );

      if (!avaliadorResponses || avaliadorResponses.length === 0) {
        return {
          hasResponses: false,
          progress: { answered: 0, total: 0, percentage: 0 },
          lastModified: null,
          syncStatus: null,
          synced: false,
          syncedAt: null,
          isComplete: false,
          avaliador: currentAvaliador,
        };
      }

      // Pegar a resposta mais recente do avaliador atual
      const latestResponse = avaliadorResponses.sort(
        (a, b) => new Date(b.lastModified || b.createdAt) - new Date(a.lastModified || a.createdAt)
      )[0];

      // Calcular progresso baseado nas respostas do avaliador atual
      const answers = latestResponse.answers || {};
      const answeredCount = Object.keys(answers).filter(key => {
        const answer = answers[key];
        return answer !== null && answer !== undefined && answer !== '';
      }).length;

      // Buscar o formulário para obter total de questões
      const form = await this.getFormById(formId);
      const totalQuestions = form?.totalQuestions || form?.requirements?.length || 0;

      const percentage =
        totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

      // Determinar status de sincronização
      let syncStatus = 'local-only';
      if (latestResponse.synced && latestResponse.syncedAt) {
        syncStatus = 'synced';
      } else if (latestResponse.cloudModified && latestResponse.lastModified) {
        const localDate = new Date(latestResponse.lastModified);
        const cloudDate = new Date(latestResponse.cloudModified);
        if (cloudDate > localDate) {
          syncStatus = 'cloud-newer';
        } else if (localDate > cloudDate) {
          syncStatus = 'local-newer';
        }
      }

      const result = {
        hasResponses: true,
        progress: {
          answered: answeredCount,
          total: totalQuestions,
          percentage: percentage,
        },
        lastModified: latestResponse.lastModified || latestResponse.createdAt,
        syncStatus: syncStatus,
        synced: latestResponse.synced || false,
        syncedAt: latestResponse.syncedAt,
        isComplete: latestResponse.isComplete || false,
        avaliador: currentAvaliador,
      };

      return result;
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas de progresso:', error);
      return {
        hasResponses: false,
        progress: { answered: 0, total: 0, percentage: 0 },
        lastModified: null,
        syncStatus: null,
        synced: false,
        syncedAt: null,
        isComplete: false,
        avaliador: this.getDefaultAvaliador(),
      };
    }
  }

  /**
   * Cria snapshot limpo para detecção de mudanças
   */
  static createOptimizedSnapshot(data) {
    return ChangeDetector.createCleanSnapshot(data);
  }
}

// Formatadores de input - DEVEM ESTAR ANTES DO COMPONENTE QUE AS USA
const formatDateInput = text => {
  const numbers = text.replace(/\D/g, '');
  const limited = numbers.substring(0, 8);

  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4, 8)}`;
  }
};

const formatTimeInput = text => {
  const numbers = text.replace(/\D/g, '');
  const limited = numbers.substring(0, 4);

  if (limited.length <= 2) {
    return limited;
  } else {
    return `${limited.slice(0, 2)}:${limited.slice(2, 4)}`;
  }
};

// Componente Modal de Edição Genérico

// Componente Modal de Edição Genérico
// Componente Modal de Edição Genérico
const EditFieldModal = ({ visible, onDismiss, type, value, onChange, onSave, error, loading }) => {
  const getTitle = () => {
    switch (type) {
      case 'title':
        return '📝 Editar Título';
      case 'description':
        return '📝 Editar Descrição';
      case 'date':
        return '📅 Editar Data da Avaliação';
      case 'time':
        return '⏰ Editar Horário da Avaliação';
      case 'location':
        return '📍 Editar Local da Avaliação';
      case 'contact':
        return '📞 Editar Contato';
      default:
        return 'Editar';
    }
  };

  const getPlaceholder = () => {
    switch (type) {
      case 'title':
        return 'Digite o título do roteiro...';
      case 'description':
        return 'Digite a descrição do roteiro...';
      case 'date':
        return 'DD/MM/AAAA';
      case 'time':
        return 'HH:MM';
      case 'location':
        return 'Digite o local da avaliação...';
      case 'contact':
        return 'Digite o contato responsável...';
      default:
        return 'Digite aqui...';
    }
  };

  const isMultiline = type === 'description';
  const keyboardType = type === 'date' || type === 'time' ? 'numeric' : 'default';
  const maxLength = type === 'date' ? 10 : type === 'time' ? 5 : 200;

  const handleChangeText = text => {
    if (type === 'date') {
      onChange(formatDateInput(text));
    } else if (type === 'time') {
      onChange(formatTimeInput(text));
    } else {
      onChange(text);
    }
  };

  return (
    <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onDismiss}>
      <KeyboardAvoidingView
        style={styles.modalOverlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={onDismiss}
        />

        <View style={styles.editModalContainer}>
          <View style={styles.editModalHeader}>
            <Text style={styles.editModalTitle}>{getTitle()}</Text>
            <IconButton icon="close" size={24} onPress={onDismiss} />
          </View>

          <View style={styles.editModalContent}>
            <TextInput
              value={value}
              onChangeText={handleChangeText}
              placeholder={getPlaceholder()}
              placeholderTextColor="#999"
              style={[styles.editModalInput, isMultiline && styles.editModalInputMultiline]}
              multiline={isMultiline}
              numberOfLines={isMultiline ? 4 : 1}
              keyboardType={keyboardType}
              maxLength={maxLength}
              autoFocus
            />

            {error && <Text style={styles.editModalError}>❌ {error}</Text>}
          </View>

          <View style={styles.editModalFooter}>
            <Button mode="text" onPress={onDismiss}>
              Cancelar
            </Button>
            <Button mode="contained" onPress={onSave} loading={loading}>
              Salvar
            </Button>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// Componente Dialog Personalizado
const CustomDialog = ({ visible, onDismiss, title, message, actions, children }) => {
  return (
    <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onDismiss}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onDismiss}>
        <TouchableOpacity
          style={styles.dialogContainer}
          activeOpacity={1}
          onPress={() => {}} // Evita fechar ao tocar no dialog
        >
          <View style={styles.dialogHeader}>
            <Text style={styles.dialogTitle}>{title}</Text>
          </View>

          <View style={styles.dialogContent}>
            {children || <Text style={styles.dialogMessage}>{message}</Text>}
          </View>

          <View style={styles.dialogActions}>
            {actions.map((action, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.dialogButton,
                  index === actions.length - 1
                    ? styles.dialogButtonPrimary
                    : styles.dialogButtonSecondary,
                ]}
                onPress={() => {
                  action.action();
                  onDismiss();
                }}
              >
                <Text
                  style={[
                    styles.dialogButtonText,
                    index === actions.length - 1
                      ? styles.dialogButtonTextPrimary
                      : styles.dialogButtonTextSecondary,
                  ]}
                >
                  {action.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

// Componente Loading Overlay
const LoadingOverlay = ({ visible, title, subtitle }) => {
  return (
    <Modal visible={visible} transparent={true} animationType="fade">
      <View style={styles.loadingOverlay}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
          <Text style={styles.loadingText}>{title}</Text>
          <Text style={styles.loadingSubtext}>{subtitle}</Text>
        </View>
      </View>
    </Modal>
  );
};

// NOVO COMPONENTE: Editor de Emails das Lideranças
const EmailEditor = ({ visible, onDismiss, formId, currentEmails, onSave }) => {
  const [emails, setEmails] = useState(currentEmails || []);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setEmails(currentEmails || []);
      setNewEmail('');
      setError('');
    }
  }, [visible, currentEmails]);

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addEmail = () => {
    if (!newEmail.trim()) {
      setError('Digite um email');
      return;
    }

    if (!validateEmail(newEmail.trim())) {
      setError('Email inválido');
      return;
    }

    if (emails.includes(newEmail.trim())) {
      setError('Email já adicionado');
      return;
    }

    if (emails.length >= 10) {
      setError('Máximo de 10 emails permitido');
      return;
    }

    setEmails([...emails, newEmail.trim()]);
    setNewEmail('');
    setError('');
  };

  const removeEmail = emailToRemove => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(emails);
      onDismiss();
    } catch (error) {
      setError('Erro ao salvar emails');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent={true} animationType="slide" onRequestClose={onDismiss}>
      <KeyboardAvoidingView
        style={styles.modalOverlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Overlay clicável para fechar o modal */}
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={onDismiss}
        />

        {/* Container do editor que não fecha ao clicar */}
        <View
          style={styles.emailEditorContainer}
          onStartShouldSetResponder={() => true}
          onMoveShouldSetResponder={() => true}
          onResponderTerminationRequest={() => false}
        >
          <View style={styles.emailEditorHeader}>
            <Text style={styles.emailEditorTitle}>📧 Editar Emails das Lideranças</Text>
            <IconButton icon="close" size={24} onPress={onDismiss} />
          </View>

          <ScrollView style={styles.emailEditorContent}>
            <View style={styles.emailInputContainer}>
              <TextInput
                style={styles.emailTextInput}
                placeholder="Digite o email..."
                placeholderTextColor="#999"
                value={newEmail}
                onChangeText={setNewEmail}
                onFocus={() => setError('')}
                onSubmitEditing={addEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
                returnKeyType="done"
              />
              <Button
                mode="contained"
                onPress={addEmail}
                disabled={!newEmail.trim() || emails.length >= 10}
                compact
                style={styles.addEmailButton}
              >
                Adicionar
              </Button>
            </View>

            {error !== '' && <Text style={styles.emailErrorText}>❌ {error}</Text>}

            <Text style={styles.emailListTitle}>Emails cadastrados ({emails.length}/10):</Text>

            {emails.length === 0 ? (
              <Text style={styles.noEmailsText}>Nenhum email cadastrado</Text>
            ) : (
              emails.map((email, index) => (
                <View key={`email-${index}`} style={styles.emailItemRow}>
                  <Text style={styles.emailItemText}>
                    {index + 1}. {email}
                  </Text>
                  <IconButton
                    icon="delete"
                    size={20}
                    iconColor="#F44336"
                    onPress={() => removeEmail(email)}
                  />
                </View>
              ))
            )}
          </ScrollView>

          <View style={styles.emailEditorFooter}>
            <Button mode="text" onPress={onDismiss} disabled={saving}>
              Cancelar
            </Button>
            <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving}>
              Salvar Alterações
            </Button>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// NOVO COMPONENTE: Seletor de Avaliador
const AvaliadorSelector = ({
  visible,
  onDismiss,
  onSelect,
  currentAvaliadorId,
  excludeAvaliadorId,
}) => {
  const [searchText, setSearchText] = useState('');

  const filteredAvaliadores = useMemo(() => {
    return AVALIADORES.filter(av => {
      if (av.id === excludeAvaliadorId) return false; // Não mostrar o outro avaliador
      const searchLower = searchText.toLowerCase();
      return (
        av.nome.toLowerCase().includes(searchLower) || av.login.toLowerCase().includes(searchLower)
      );
    });
  }, [searchText, excludeAvaliadorId]);

  return (
    <Modal visible={visible} transparent={true} animationType="slide" onRequestClose={onDismiss}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onDismiss}>
        <View style={styles.avaliadorSelectorContainer} onStartShouldSetResponder={() => true}>
          <View style={styles.avaliadorSelectorHeader}>
            <Text style={styles.avaliadorSelectorTitle}>Selecionar Avaliador</Text>
            <IconButton icon="close" size={24} onPress={onDismiss} />
          </View>

          <View style={styles.searchContainer}>
            <MaterialCommunityIcons name="magnify" size={20} color="#666" />
            <TextInput
              style={styles.searchInput}
              placeholder="Buscar avaliador..."
              value={searchText}
              onChangeText={setSearchText}
              placeholderTextColor="#999"
            />
          </View>

          <ScrollView
            style={styles.avaliadorList}
            contentContainerStyle={styles.avaliadorListContent}
          >
            {filteredAvaliadores.map(avaliador => (
              <TouchableOpacity
                key={avaliador.id}
                style={[
                  styles.avaliadorItem,
                  avaliador.id === currentAvaliadorId && styles.avaliadorItemSelected,
                ]}
                onPress={() => {
                  onSelect(avaliador);
                  setSearchText('');
                  onDismiss();
                }}
              >
                <View style={styles.avaliadorItemContent}>
                  <Text style={styles.avaliadorItemName}>{avaliador.nome}</Text>
                  <Text style={styles.avaliadorItemEmail}>{avaliador.login}</Text>
                </View>
                {avaliador.id === currentAvaliadorId && (
                  <MaterialCommunityIcons
                    name="check"
                    size={24}
                    color={AVAQualisTheme.colors.primary}
                  />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </TouchableOpacity>
    </Modal>
  );
};

// NOVO COMPONENTE: Convite para Login
const LoginInviteCard = ({ onLogin }) => {
  return (
    <Card style={styles.loginInviteCard}>
      <Card.Content>
        <View style={styles.loginInviteContent}>
          {/* Ícone principal animado */}
          <View style={styles.iconContainer}>
            <MaterialCommunityIcons
              name="shield-check"
              size={80}
              color={AVAQualisTheme.colors.primary}
            />
            <View style={styles.iconBadge}>
              <MaterialCommunityIcons name="lock-open" size={24} color="#FFFFFF" />
            </View>
          </View>

          {/* Título principal */}
          <Title style={styles.loginInviteTitle}>Bem-vindo ao AVAQualis! 👋</Title>

          {/* Subtítulo */}
          <Paragraph style={styles.loginInviteSubtitle}>Olá, Avaliador(a)!</Paragraph>

          {/* Mensagem principal */}
          <Paragraph style={styles.loginInviteMessage}>
            Para acessar seus roteiros de avaliação e começar a usar a ferramenta, você precisa
            fazer login com suas credenciais do AVAQualis.com.
          </Paragraph>

          {/* Lista de benefícios */}
          <View style={styles.benefitsList}>
            <View style={styles.benefitItem}>
              <MaterialCommunityIcons
                name="check-circle"
                size={20}
                color={AVAQualisTheme.colors.success}
              />
              <Text style={styles.benefitText}>Acesse seus roteiros personalizados</Text>
            </View>

            <View style={styles.benefitItem}>
              <MaterialCommunityIcons
                name="check-circle"
                size={20}
                color={AVAQualisTheme.colors.success}
              />
              <Text style={styles.benefitText}>Preencha avaliações offline</Text>
            </View>

            <View style={styles.benefitItem}>
              <MaterialCommunityIcons
                name="check-circle"
                size={20}
                color={AVAQualisTheme.colors.success}
              />
              <Text style={styles.benefitText}>Sincronize com o servidor quando tiver conexão</Text>
            </View>
          </View>

          {/* Botão de Login */}
          <Button
            mode="contained"
            onPress={onLogin}
            icon="login"
            style={styles.loginButton}
            contentStyle={styles.loginButtonContent}
            labelStyle={styles.loginButtonLabel}
          >
            Fazer Login
          </Button>

          {/* Texto adicional */}
          <Text style={styles.footerText}>Protótipo desenvolvido por UGITS + UGQ</Text>
        </View>
      </Card.Content>
    </Card>
  );
};

// Estado de conexão com o servidor
const ConnectionStatus = React.memo(({ isOnline, onCheck }) => {
  return (
    <TouchableOpacity
      onPress={onCheck}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: isOnline ? 'rgba(0,200,0,0.2)' : 'rgba(255,0,0,0.2)',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        marginRight: 8,
      }}
    >
      <Icon
        source={isOnline ? 'cloud-check' : 'cloud-off-outline'}
        size={16}
        color={isOnline ? '#4CAF50' : '#F44336'}
      />
      <Text
        style={{
          color: isOnline ? '#2E7D32' : '#C62828',
          fontSize: 12,
          marginLeft: 4,
        }}
      >
        {isOnline ? 'Online' : 'Offline'}
      </Text>
    </TouchableOpacity>
  );
});

// Componente para limpar armazenamento
const StorageCleanupButton = React.memo(({ onCleanup }) => {
  return (
    <TouchableOpacity
      onPress={onCleanup}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(255,152,0,0.2)',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        marginRight: 8,
      }}
    >
      <Icon source="broom" size={16} color="#FF9800" />
      <Text
        style={{
          color: '#E65100',
          fontSize: 12,
          marginLeft: 4,
        }}
      >
        Limpar Cache
      </Text>
    </TouchableOpacity>
  );
});

// Link para criar formulário
const CreateFormLink = React.memo(({ onCreateForm }) => {
  return (
    <TouchableOpacity
      onPress={onCreateForm}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: AVAQualisTheme.colors.primary,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 25,
        marginVertical: 16,
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 4,
      }}
    >
      <Icon source="plus-circle" size={20} color={AVAQualisTheme.colors.onPrimary} />
      <Text
        style={{
          color: AVAQualisTheme.colors.onPrimary,
          fontSize: 14,
          fontWeight: 'bold',
          marginLeft: 8,
          marginRight: 4,
        }}
      >
        Criar Novo Roteiro
      </Text>
      <Icon source="file-document-edit" size={18} color={AVAQualisTheme.colors.onPrimary} />
    </TouchableOpacity>
  );
});

// COMPONENTE CORRIGIDO 3: FormStatusIndicator com lógica de priorização correta
const FormStatusIndicator = React.memo(({ form, formId, progressStats }) => {
  const statusInfo = useMemo(() => {
    const progress = progressStats?.progress?.percentage || 0;
    const hasResponses = progressStats?.hasResponses || false;
    const isComplete = progressStats?.isComplete || false;
    const syncStatus = progressStats?.syncStatus || 'local-only';
    const isSynced = progressStats?.synced || false;
    const lastModified = progressStats?.lastModified;

    let backgroundColor, textColor, label, icon, message;

    // CORREÇÃO 3: Verificar sincronização PRIMEIRO (prioridade máxima)
    if (isSynced && progressStats?.syncedAt) {
      backgroundColor = '#E8F5E9';
      textColor = '#2E7D32';
      label = 'Enviado para nuvem';
      icon = '✅';
      message = `Formulário sincronizado (${progress}% completo)`;
    }
    // DEPOIS verificar se tem respostas
    else if (hasResponses && progress > 0 && progress < 100) {
      backgroundColor = '#FFF8E1';
      textColor = '#FF6F00';
      label = `${progress}% completo`;
      icon = '✏️';
      message = 'Continue preenchendo o formulário.';
    }
    // Se está completo mas não sincronizado
    else if (progress === 100 && !isSynced) {
      backgroundColor = '#FFEBEE';
      textColor = '#D32F2F';
      label = 'Pronto para enviar';
      icon = '📤';
      message = 'Formulário completo. Envie para a nuvem.';
    }
    // Se não tem respostas (por último)
    else if (!hasResponses || progress === 0) {
      backgroundColor = '#E3F2FD';
      textColor = '#1976D2';
      label = 'Não iniciado';
      icon = '📋';
      message = 'Formulário pronto para preenchimento.';
    }

    return { backgroundColor, textColor, label, icon, message, lastModified };
  }, [progressStats]);

  return (
    <View
      style={[
        styles.formStatusIndicatorCompact,
        { backgroundColor: statusInfo.backgroundColor, borderLeftColor: statusInfo.textColor },
      ]}
    >
      <View style={styles.statusCompactRow}>
        <View style={styles.statusIconContainer}>
          <Text style={styles.statusIcon}>{statusInfo.icon}</Text>
        </View>
        <View style={styles.statusTextContainer}>
          <Text style={[styles.statusLabelCompact, { color: statusInfo.textColor }]}>
            {statusInfo.label}
          </Text>
          <Text style={[styles.statusMessageCompact, { color: statusInfo.textColor }]}>
            {statusInfo.message}
          </Text>
        </View>
        {statusInfo.lastModified && (
          <View style={styles.statusDateContainer}>
            <Text style={[styles.statusDateCompact, { color: statusInfo.textColor }]}>
              {new Date(statusInfo.lastModified).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
});

// COMPONENTE CORRIGIDO 1: Indicador de progresso com percentual correto
const FormProgressIndicator = React.memo(({ form, progressStats }) => {
  const progressInfo = useMemo(() => {
    // CORREÇÃO 1: Usar valores corretos do progressStats
    const progress = progressStats?.progress?.percentage || 0;

    // DEBUG: Verificar o valor real
    console.log(`🎯 FormProgressIndicator - Percentual recebido: ${progress}%`);

    const answeredCount = progressStats?.progress?.answered || 0;
    const totalQuestions = progressStats?.progress?.total || form.totalQuestions || 0;
    const syncStatus = progressStats?.syncStatus || 'local-only';
    const hasResponses = progressStats?.hasResponses || false;
    const isSynced = progressStats?.synced || false;

    // Adicionar informações de sincronização ao progressInfo
    return {
      progress, // CORREÇÃO 1: Usar o percentual correto (já está em 0-100)
      answeredCount,
      totalQuestions,
      syncStatus,
      hasResponses,
      isSynced,
    };
  }, [form.totalQuestions, progressStats]);

  // Determinar cores com base no status
  let progressColor, backgroundColor, textColor;

  switch (progressInfo.syncStatus) {
    case 'synced':
      progressColor = '#2E7D32'; // Verde para sincronizado
      backgroundColor = '#E8F5E9';
      textColor = '#2E7D32';
      break;
    case 'cloud-newer':
      progressColor = '#D32F2F'; // Vermelho para nuvem mais nova
      backgroundColor = '#FFEBEE';
      textColor = '#D32F2F';
      break;
    case 'local-newer':
      progressColor = '#FF6F00'; // Laranja para local mais novo
      backgroundColor = '#FFF8E1';
      textColor = '#FF6F00';
      break;
    case 'local-only':
      progressColor = '#FF8F00'; // Amarelo para somente local
      backgroundColor = '#FFECB3';
      textColor = '#FF8F00';
      break;
    default:
      progressColor = AVAQualisTheme.colors.primary;
      backgroundColor = `${AVAQualisTheme.colors.primary}20`;
      textColor = AVAQualisTheme.colors.primary;
  }

  // Se não há respostas, usar cor neutra
  if (!progressInfo.hasResponses) {
    progressColor = '#E0E0E0';
    backgroundColor = '#FAFAFA';
    textColor = '#757575';
  }

  return (
    <View style={[styles.progressIndicatorContainer, { backgroundColor: backgroundColor }]}>
      <View style={styles.progressHeader}>
        <Text style={[styles.progressPercentage, { color: textColor }]}>
          <Text style={styles.progressPercentageBold}>{progressInfo.progress}%</Text> concluído
        </Text>

        <Text style={[styles.progressFraction, { color: textColor }]}>
          <Text style={styles.progressBoldNumber}>{progressInfo.answeredCount}</Text>/
          {<Text style={styles.progressBoldNumber}>{progressInfo.totalQuestions}</Text>} perguntas
          respondidas
        </Text>
      </View>

      <View style={styles.progressBarContainer}>
        <View style={[styles.progressBarTrack, { backgroundColor: `${progressColor}30` }]}>
          <View
            style={[
              styles.progressBarFill,
              {
                backgroundColor: progressColor,
                // CORREÇÃO 1: width já usa o percentual correto
                width: `${progressInfo.progress}%`,
              },
            ]}
          />
        </View>
      </View>

      {/* Mensagens de status baseadas no progresso real */}
      {!progressInfo.hasResponses && (
        <Text style={styles.progressStatusText}>
          📋 Este formulário ainda não foi iniciado. Clique em "Iniciar" para começar a responder.
        </Text>
      )}

      {progressInfo.hasResponses && progressInfo.progress > 0 && progressInfo.progress < 100 && (
        <Text style={styles.progressStatusText}>
          ✏️ Formulário em progresso. Continue de onde parou ou salve suas respostas.
        </Text>
      )}

      {progressInfo.syncStatus === 'synced' && progressInfo.progress === 100 && (
        <Text style={styles.progressStatusText}>
          ✅ Este formulário está completo e sincronizado com a nuvem. Tudo correto e atualizado.
        </Text>
      )}

      {progressInfo.syncStatus === 'local-newer' && progressInfo.progress === 100 && (
        <Text style={styles.progressStatusText}>
          ⚠️ Formulário completo! Sua versão local está mais recente. Faça upload dessa versão para
          evitar perder informações.
        </Text>
      )}

      {progressInfo.syncStatus === 'local-only' && progressInfo.progress === 100 && (
        <Text style={styles.progressStatusText}>
          ⚠️ Formulário completo! Está salvo somente no seu dispositivo. Faça upload imediatamente
          para evitar perda de informações.
        </Text>
      )}

      {progressInfo.syncStatus === 'cloud-newer' && (
        <Text style={styles.progressStatusText}>
          ⚠️ Existe uma versão mais recente deste formulário na nuvem! Recomenda-se baixar esta
          versão antes de continuar.
        </Text>
      )}
    </View>
  );
});

// HOOK PERSONALIZADO: Debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// HOOK PERSONALIZADO: Throttle
const useThrottle = (callback, delay) => {
  const lastRun = useRef(Date.now());

  return useCallback(
    (...args) => {
      if (Date.now() - lastRun.current >= delay) {
        lastRun.current = Date.now();
        callback(...args);
      }
    },
    [callback, delay]
  );
};

// Componente Principal - COMPLETAMENTE CORRIGIDO PARA ELIMINAR LOOPS INFINITOS
export default function FormListScreen({
  onSelectForm,
  onViewResponses,
  onCreateForm,
  currentUser,
  isVisitor,
  canCreateForm,
  isSimulating,
  simulatedUser,
  onOpenLogin,
  navigation, // CORREÇÃO 2: navigation para o efeito de focus
}) {
  // CORREÇÃO CRÍTICA: Estados principais simplificados
  const [allForms, setAllForms] = useState([]);
  const [formProgressStats, setFormProgressStats] = useState({});

  // MUDANÇA 1: ADICIONAR NOVO ESTADO para controle de carregamento de estatísticas
  const [loadingStats, setLoadingStats] = useState(new Set());
  const [forceUpdateTrigger, setForceUpdateTrigger] = useState(0);

  // Estados de carregamento controlados
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingFormId, setDownloadingFormId] = useState(null);
  const [uploadingFormId, setUploadingFormId] = useState(null);

  // Estados de UI
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    title: '',
    message: '',
    actions: [],
  });
  const [isServerOnline, setIsServerOnline] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [serverError, setServerError] = useState(null);

  // Estado para seletor de avaliador
  const [avaliadorSelectorVisible, setAvaliadorSelectorVisible] = useState(false);
  const [editingAvaliadorPosition, setEditingAvaliadorPosition] = useState(null); // 1 ou 2
  const [editingFormId, setEditingFormId] = useState(null);
  const [editingFormIdForEmails, setEditingFormIdForEmails] = useState(null);
  const [emailsLiderancas, setEmailsLiderancas] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [savingEmails, setSavingEmails] = useState(false);

  // Estado para modal de envio de email
  const [emailModalFormId, setEmailModalFormId] = useState(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);

  // Estados para edição de campos
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalType, setEditModalType] = useState(''); // 'title', 'description', 'date', 'time', 'location', 'contact'
  const [editModalFormId, setEditModalFormId] = useState(null);
  const [editModalValue, setEditModalValue] = useState('');
  const [editModalError, setEditModalError] = useState('');

  // Estados para edição de unidades organizacionais
  const [unidadeEditModalVisible, setUnidadeEditModalVisible] = useState(false);
  const [unidadeEditFormId, setUnidadeEditFormId] = useState(null);
  const [unidadeEditSelectedUnidades, setUnidadeEditSelectedUnidades] = useState([]);
  const [unidadeEditSearchText, setUnidadeEditSearchText] = useState('');
  const [unidadeEditFilteredUnidades, setUnidadeEditFilteredUnidades] = useState([]);
  const [unidadeEditShowDropdown, setUnidadeEditShowDropdown] = useState(false);

  // Estados para edição de processo avaliado
  const [processoEditModalVisible, setProcessoEditModalVisible] = useState(false);
  const [processoEditFormId, setProcessoEditFormId] = useState(null);
  const [processoEditSelectedProcessos, setProcessoEditSelectedProcessos] = useState([]);
  const [processoEditSearchText, setProcessoEditSearchText] = useState('');
  const [processoEditFilteredProcessos, setProcessoEditFilteredProcessos] = useState([]);
  const [processoEditShowDropdown, setProcessoEditShowDropdown] = useState(false);
  const [processoEditAllProcessos, setProcessoEditAllProcessos] = useState([]);
  const [processoEditingId, setProcessoEditingId] = useState(null);
  const [processoEditingText, setProcessoEditingText] = useState('');

  // MUDANÇA 1: ADICIONAR novo estado para status de sincronização
  const [formSyncStatus, setFormSyncStatus] = useState({});
  const [formValidations, setFormValidations] = useState({});

  // CORREÇÃO CRÍTICA: Refs estáveis para evitar loops infinitos
  const loadDataTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const storageListenerRef = useRef(null);
  const lastUpdateTime = useRef(0);
  const mountedRef = useRef(true);

  // CORREÇÃO CRÍTICA: Cache estável para dados
  const [cachedData, setCachedData] = useState({
    customForms: [],
    serverForms: [],
    formResponses: [],
    downloadedFormIds: new Set(),
  });

  // NOVA ADIÇÃO: Ref para controle de salvamento otimizado
  const optimizedSaveRef = useRef({
    lastSaveTime: 0,
    pendingSaves: new Map(),
    saveQueue: [],
  });

  // CORREÇÃO CRÍTICA: Filtrar formulários baseado no usuário efetivo - ESTÁVEL
  const filterFormsByUser = useCallback(
    forms => {
      const effectiveUser = isSimulating && simulatedUser ? simulatedUser : currentUser;

      if (!effectiveUser) {
        console.warn('⚠️ Nenhum usuário efetivo encontrado');
        return [];
      }

      // Se for visitante, mostrar todos (mas sem poder criar)
      if (effectiveUser.id === 'visitor') {
        return forms;
      }

      // Se for membro (efetivo, seja real ou simulado), filtrar apenas onde é avaliador
      if (effectiveUser.level === 'membro') {
        const filteredForms = forms.filter(form => {
          // Verificar se o formulário tem metadata
          if (!form.metadata) return false;

          // Verificar se o usuário é avaliador1 ou avaliador2
          const isAvaliador =
            form.metadata.avaliador1 === effectiveUser.id ||
            form.metadata.avaliador2 === effectiveUser.id;

          return isAvaliador;
        });

        return filteredForms;
      }

      // Se for coordenação ou programador, mostrar todos
      if (effectiveUser.level === 'coordenacao' || effectiveUser.level === 'programador') {
        return forms;
      }

      console.warn('⚠️ Nível de usuário não reconhecido:', effectiveUser.level);
      return [];
    },
    [isSimulating, simulatedUser?.id, currentUser?.id]
  );

  // NOVA FUNÇÃO: Salvamento otimizado para evitar lastModified desnecessário
  const performOptimizedSave = useCallback(
    async (responseData, isAutoSave = false, isComplete = false) => {
      try {
        // Usar o StorageManager otimizado
        const result = await OptimizedStorageManager.saveResponseDataOptimized(
          responseData,
          isAutoSave,
          isComplete
        );

        if (result) {
          console.log(`✅ Salvamento otimizado ${isAutoSave ? '(auto)' : '(manual)'} realizado`);

          // Atualizar estatísticas apenas se realmente salvou
          if (mountedRef.current) {
            const updatedStats = await OptimizedStorageManager.getFormProgressStats(
              responseData.formId
            );
            setFormProgressStats(prev => ({
              ...prev,
              [responseData.formId]: updatedStats,
            }));
          }
        }

        return result;
      } catch (error) {
        console.error('❌ Erro no salvamento otimizado:', error);
        return false;
      }
    },
    []
  );

  // NOVA FUNÇÃO: Auto-save inteligente com verificação de mudanças
  const performIntelligentAutoSave = useCallback(
    async (formId, currentData) => {
      try {
        const existingResponse = await OptimizedStorageManager.getFormResponse(
          formId,
          currentData.id
        );

        if (existingResponse) {
          const hasRealChanges = await OptimizedStorageManager.hasSignificantChanges(
            existingResponse,
            {
              answers: currentData.answers,
              findings: currentData.findings,
              savedFindings: currentData.savedFindings,
              notApplicableReasons: currentData.notApplicableReasons,
              attachments: currentData.attachments,
            }
          );

          if (!hasRealChanges) {
            console.log('🔄 Auto-save cancelado: sem mudanças reais');
            return true;
          }
        }

        return await performOptimizedSave(currentData, true, false);
      } catch (error) {
        console.error('❌ Erro no auto-save inteligente:', error);
        return false;
      }
    },
    [performOptimizedSave]
  );

  // FUNÇÃO COMPLETAMENTE CORRIGIDA: updateAllForms SEM dependências circulares
  const updateAllForms = useCallback(
    (localForms, serverForms) => {
      if (!Array.isArray(localForms) || !Array.isArray(serverForms)) {
        console.error('❌ updateAllForms: parâmetros inválidos');
        return;
      }

      // Criar mapa combinado
      const formsMap = new Map();

      // Processar formulários locais
      localForms.forEach(form => {
        if (form && form.id) {
          formsMap.set(form.id, {
            ...form,
            isDownloaded: true,
            lastModified: form.updatedAt || form.createdAt,
          });
        }
      });

      // Processar formulários do servidor
      serverForms.forEach(serverForm => {
        if (serverForm && serverForm.id) {
          const localForm = formsMap.get(serverForm.id);
          const isDownloaded = !!localForm;

          if (!localForm) {
            // Se não existir localmente, adicionar o do servidor
            formsMap.set(serverForm.id, {
              ...serverForm,
              isDownloaded: false,
              lastModified: serverForm.updatedAt || serverForm.createdAt,
            });
          } else {
            // Se existir localmente, manter dados locais mas atualizar informações da nuvem
            formsMap.set(serverForm.id, {
              ...localForm,
              isDownloaded: true,
              cloudModified: serverForm.updatedAt || serverForm.createdAt,
            });
          }
        }
      });

      // Converter para array e aplicar filtro
      const combined = Array.from(formsMap.values());
      const filteredCombined = filterFormsByUser(combined);

      // Ordenar por prioridade
      filteredCombined.sort((a, b) => {
        // 1. Primeiro critério: baixados primeiro
        if (a.isDownloaded !== b.isDownloaded) {
          return a.isDownloaded ? -1 : 1;
        }

        // 2. Por data (mais recentes primeiro)
        const dateA = new Date(a.updatedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.createdAt);
        return dateB - dateA;
      });

      if (mountedRef.current) {
        setAllForms(filteredCombined);
      }
    },
    [filterFormsByUser]
  );

  // MUDANÇA 2: ADICIONAR nova função para carregar status de sincronização
  const loadFormSyncStatus = useCallback(
    async formIds => {
      if (!formIds || formIds.size === 0 || !mountedRef.current) {
        return;
      }

      try {
        const syncStatus = {};
        const currentAvaliador = StorageManager.getDefaultAvaliador();

        if (!currentAvaliador || currentAvaliador.id === 'visitor') {
          return;
        }

        for (const formId of formIds) {
          try {
            // Buscar status local
            const localStatus = await StorageManager.getResponseSyncStatus(
              formId,
              currentAvaliador.id
            );

            // Buscar status do servidor se online
            if (isServerOnline) {
              const { getResponseSyncStatus } = await import('../lib/api');
              const serverStatus = await getResponseSyncStatus(formId, currentAvaliador.id);

              syncStatus[formId] = {
                ...localStatus,
                ...serverStatus,
              };
            } else {
              syncStatus[formId] = localStatus;
            }
          } catch (error) {
            console.error(`❌ Erro ao carregar status de sincronização para ${formId}:`, error);
          }
        }

        if (mountedRef.current) {
          setFormSyncStatus(prev => ({
            ...prev,
            ...syncStatus,
          }));
        }
      } catch (error) {
        console.error('❌ Erro ao carregar status de sincronização:', error);
      }
    },
    [isServerOnline]
  );

  // MUDANÇA 2: FUNÇÃO COMPLETAMENTE REESCRITA para buscar do servidor
  // NOVA FUNÇÃO: Carregar estatísticas usando a mesma lógica do ResponsesScreen
  const loadFormProgressStats = useCallback(
    async (formIds, forceReload = false) => {
      if (!formIds || formIds.size === 0 || !mountedRef.current) {
        return;
      }

      // Adicionar flag para evitar múltiplas execuções simultâneas
      if (loadingStats.has('all') && !forceReload) {
        console.log('⏳ Já carregando estatísticas, aguardando...');
        return;
      }

      setLoadingStats(new Set(['all']));

      try {
        const progressStats = {};
        const currentAvaliador = StorageManager.getDefaultAvaliador();

        if (!currentAvaliador || currentAvaliador.id === 'visitor') {
          setLoadingStats(new Set());
          return;
        }

        // Limpar cache se necessário (quando houver erro de quota)
        let needsCacheClear = false;

        for (const formId of formIds) {
          try {
            let foundOnServer = false;

            // SEMPRE tentar servidor primeiro se online
            if (isServerOnline) {
              try {
                console.log(`🔍 Buscando stats do servidor para ${formId}...`);

                // Buscar respostas do servidor
                const serverResponses = await api.getResponsesByFormAndAvaliador(
                  formId,
                  currentAvaliador.id
                );

                if (serverResponses && serverResponses.length > 0) {
                  foundOnServer = true;

                  // Usar a resposta mais recente do servidor
                  const latestServerResponse = serverResponses.sort(
                    (a, b) =>
                      new Date(b.lastModified || b.createdAt) -
                      new Date(a.lastModified || a.createdAt)
                  )[0];

                  // Calcular progresso
                  const answers = latestServerResponse.answers || {};
                  const answeredCount = Object.keys(answers).filter(key => {
                    const answer = answers[key];
                    return answer !== null && answer !== undefined && answer !== '';
                  }).length;

                  let totalQuestions = latestServerResponse.totalQuestions || 0;

                  if (totalQuestions === 0) {
                    try {
                      const form = await api.getFormById(formId);
                      totalQuestions = form?.totalQuestions || form?.requirements?.length || 0;
                    } catch (formError) {
                      console.warn('⚠️ Erro ao buscar totalQuestions:', formError);
                    }
                  }

                  const percentage =
                    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

                  // CORREÇÃO: Garantir que o percentual seja preservado
                  console.log(
                    `📊 Debug percentual: ${answeredCount}/${totalQuestions} = ${percentage}%`
                  );

                  progressStats[formId] = {
                    hasResponses: true,
                    progress: {
                      answered: answeredCount,
                      total: totalQuestions,
                      percentage: percentage, // IMPORTANTE: Este valor deve estar correto
                    },
                    lastModified:
                      latestServerResponse.lastModified || latestServerResponse.createdAt,
                    syncStatus: 'synced',
                    synced: true,
                    syncedAt: latestServerResponse.syncedAt || latestServerResponse.lastModified,
                    isComplete: latestServerResponse.isComplete || percentage === 100,
                    responseId: latestServerResponse.id,
                    avaliador: currentAvaliador,
                    fromServer: true, // Flag para indicar que veio do servidor
                  };

                  console.log(`✅ Stats do servidor carregadas para ${formId}: ${percentage}%`);

                  // Salvar no StorageManager para persistir
                  try {
                    await StorageManager.saveResponse({
                      ...latestServerResponse,
                      synced: true,
                      syncedAt: latestServerResponse.syncedAt || new Date().toISOString(),
                    });
                  } catch (saveError) {
                    if (saveError.message && saveError.message.includes('QuotaExceededError')) {
                      needsCacheClear = true;
                      console.warn('⚠️ Storage cheio detectado, limpando automaticamente...');

                      // Limpar cache imediatamente
                      await clearOldAsyncStorageData();

                      // Tentar salvar novamente
                      try {
                        await StorageManager.saveResponse({
                          ...latestServerResponse,
                          synced: true,
                          syncedAt: latestServerResponse.syncedAt || new Date().toISOString(),
                        });
                        console.log('✅ Resposta salva após limpeza automática');
                      } catch (retryError) {
                        console.warn('⚠️ Ainda sem espaço após limpeza:', retryError);
                      }
                    } else {
                      console.warn('⚠️ Erro ao salvar resposta localmente:', saveError);
                    }
                  }
                }
              } catch (serverError) {
                console.log(`⚠️ Erro ao buscar do servidor:`, serverError);
                if (serverError.message && serverError.message.includes('QuotaExceededError')) {
                  needsCacheClear = true;
                }
              }
            }

            // Só buscar localmente se NÃO encontrou no servidor
            if (!foundOnServer) {
              // SEMPRE buscar localmente também
              console.log(`📱 Buscando stats locais para ${formId}...`);

              const allResponses = await StorageManager.loadFormResponses();
              const formResponses = allResponses.filter(
                response =>
                  response.formId === formId &&
                  response.avaliador &&
                  response.avaliador.id === currentAvaliador.id
              );

              if (formResponses.length > 0) {
                const latestResponse = formResponses.sort(
                  (a, b) =>
                    new Date(b.lastModified || b.createdAt) -
                    new Date(a.lastModified || a.createdAt)
                )[0];

                const answers = latestResponse.answers || {};
                const answeredCount = Object.keys(answers).filter(key => {
                  const answer = answers[key];
                  return answer !== null && answer !== undefined && answer !== '';
                }).length;

                let totalQuestions = latestResponse.totalQuestions || 0;

                if (totalQuestions === 0) {
                  const forms = await StorageManager.loadCustomForms();
                  const form = forms.find(f => f.id === formId);
                  if (form) {
                    totalQuestions =
                      form.totalQuestions ||
                      (form.requirements ? form.requirements.length : 0) ||
                      (form.questions ? form.questions.length : 0);
                  }
                }

                const percentage =
                  totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

                let syncStatus = 'local-only';
                if (latestResponse.synced && latestResponse.syncedAt) {
                  syncStatus = 'synced';
                }

                // IMPORTANTE: Só sobrescrever se não encontrou no servidor OU se local tem dados
                if (!foundOnServer || percentage > 0) {
                  progressStats[formId] = {
                    hasResponses: true,
                    progress: {
                      answered: answeredCount,
                      total: totalQuestions,
                      percentage: percentage,
                    },
                    lastModified: latestResponse.lastModified || latestResponse.createdAt,
                    syncStatus: foundOnServer ? 'synced' : syncStatus,
                    synced: foundOnServer ? true : latestResponse.synced || false,
                    syncedAt: foundOnServer ? new Date().toISOString() : latestResponse.syncedAt,
                    isComplete: latestResponse.isComplete || percentage === 100,
                    responseId: latestResponse.id,
                    avaliador: currentAvaliador,
                    fromServer: false,
                  };

                  console.log(`✅ Stats locais carregadas para ${formId}: ${percentage}%`);
                }
              } else if (!foundOnServer) {
                // Só definir como vazio se NÃO encontrou no servidor E NÃO tem local
                progressStats[formId] = {
                  hasResponses: false,
                  progress: { answered: 0, total: 0, percentage: 0 },
                  lastModified: null,
                  syncStatus: null,
                  synced: false,
                  syncedAt: null,
                  isComplete: false,
                  avaliador: currentAvaliador,
                  fromServer: false,
                };
              }
            }
          } catch (error) {
            console.error(`❌ Erro ao carregar progresso do formulário ${formId}:`, error);
            progressStats[formId] = {
              hasResponses: false,
              progress: { answered: 0, total: 0, percentage: 0 },
              lastModified: null,
              syncStatus: null,
              synced: false,
              syncedAt: null,
              isComplete: false,
              fromServer: false,
            };
          }
        }

        // Limpar cache se necessário
        if (needsCacheClear) {
          console.log('⚠️ Limpando cache devido a erro de quota...');
          await clearOldAsyncStorageData();
        }

        if (mountedRef.current) {
          // Atualizar estado preservando dados do servidor
          // Atualizar estado preservando dados do servidor
          setFormProgressStats(prev => {
            const newStats = { ...prev };

            // CORREÇÃO: Forçar atualização garantindo que o percentual seja preservado
            Object.keys(progressStats).forEach(formId => {
              const newStat = progressStats[formId];
              const existingStat = prev[formId];

              // Se a nova stat é do servidor, sempre usar
              // Se não existe stat anterior, usar a nova
              // Se a existente não é do servidor, pode atualizar
              // CORREÇÃO: Nunca sobrescrever progresso > 0 com 0
              if (newStat.fromServer || !existingStat || !existingStat.fromServer) {
                // Se existe progresso anterior > 0, não sobrescrever com 0
                if (
                  existingStat &&
                  existingStat.progress.percentage > 0 &&
                  newStat.progress.percentage === 0
                ) {
                  console.log(
                    `⚠️ Mantendo progresso ${existingStat.progress.percentage}% para ${formId} (tentou sobrescrever com 0)`
                  );
                  // Manter o existente mas atualizar flag de sincronização se veio do servidor
                  if (newStat.fromServer) {
                    newStats[formId] = {
                      ...existingStat,
                      synced: true,
                      syncedAt: new Date().toISOString(),
                      syncStatus: 'synced',
                    };
                  }
                } else {
                  // Caso normal - atualizar
                  newStats[formId] = {
                    ...newStat,
                    progress: {
                      ...newStat.progress,
                      percentage: newStat.progress.percentage || 0,
                    },
                  };
                  console.log(
                    `✅ Atualizando stats para ${formId}: ${newStat.progress.percentage}%`
                  );
                }
              }
            });

            return newStats;
          });

          setForceUpdateTrigger(prev => prev + 1);
        }
      } catch (error) {
        console.error('❌ Erro ao carregar estatísticas de progresso:', error);
      } finally {
        setLoadingStats(new Set());
      }
    },
    [isServerOnline]
  );
  // MUDANÇA 3: MODIFICAR o useEffect que responde a mudanças no StorageManager
  useEffect(() => {
    const handleFormResponseUpdate = async (changeType, data) => {
      if (!mountedRef.current) return;

      if (
        changeType === 'response_saved' ||
        changeType === 'response_removed' ||
        changeType === 'response_synced' ||
        changeType === 'response_sync_updated'
      ) {
        if (data.formId) {
          console.log(`🔄 Atualizando stats para formulário ${data.formId} após ${changeType}`);
          const formIds = new Set([data.formId]);

          // Aguardar um pouco para garantir que os dados foram salvos
          setTimeout(async () => {
            await loadFormProgressStats(formIds, true);
          }, 500);
        }
      }
    };

    const removeListener = OptimizedStorageManager.addChangeListener(handleFormResponseUpdate);

    return () => {
      if (removeListener) removeListener();
    };
  }, [loadFormProgressStats]);

  // MUDANÇA 4: ADICIONAR monitoramento de conexão
  useEffect(() => {
    const checkInterval = setInterval(async () => {
      await checkServerConnection();
    }, 30000); // Verificar a cada 30 segundos

    return () => clearInterval(checkInterval);
  }, []);

  // Quando a conexão mudar, recarregar stats
  useEffect(() => {
    if (isServerOnline && allForms.length > 0) {
      const formIds = new Set(allForms.map(f => f.id));
      loadFormProgressStats(formIds, true);
    }
  }, [isServerOnline, allForms, loadFormProgressStats]);

  // Filtrar unidades para edição
  useEffect(() => {
    if (!unidadeEditSearchText.trim()) {
      setUnidadeEditFilteredUnidades([]);
      setUnidadeEditShowDropdown(false);
      return;
    }

    const filtered = searchUnidades(unidadeEditSearchText);
    const filteredWithoutSelected = filtered.filter(
      unidade => !unidadeEditSelectedUnidades.some(selected => selected.id === unidade.id)
    );

    setUnidadeEditFilteredUnidades(filteredWithoutSelected.slice(0, 10));
    setUnidadeEditShowDropdown(filteredWithoutSelected.length > 0);
  }, [unidadeEditSearchText, unidadeEditSelectedUnidades]);

  // Filtrar processos para edição
  useEffect(() => {
    if (!processoEditSearchText.trim()) {
      setProcessoEditFilteredProcessos([]);
      setProcessoEditShowDropdown(false);
      return;
    }

    const searchLower = processoEditSearchText.toLowerCase();
    const filtered = processoEditAllProcessos.filter(
      processo =>
        processo.nome.toLowerCase().includes(searchLower) &&
        !processoEditSelectedProcessos.some(p => p.id === processo.id)
    );

    setProcessoEditFilteredProcessos(filtered);
    setProcessoEditShowDropdown(true);
  }, [processoEditSearchText, processoEditAllProcessos, processoEditSelectedProcessos]);

  // LISTENER CORRIGIDO: Para mudanças no StorageManager - THROTTLED
  const handleStorageChange = useThrottle(
    useCallback(
      (changeType, data) => {
        if (!mountedRef.current) return;

        console.log('📢 Storage change:', changeType);

        switch (changeType) {
          case 'response_saved':
          case 'response_removed':
          case 'response_updated': // ADICIONAR este caso
            // Atualizar estatísticas IMEDIATAMENTE
            if (data.formId) {
              console.log(`🔄 Atualizando stats IMEDIATAMENTE para formulário ${data.formId}`);

              // Executar sem delay
              const formIds = new Set([data.formId]);
              loadFormProgressStats(formIds, true);
              // Recarregar validações também
              getFormValidations(data.formId).then(validations => {
                if (mountedRef.current) {
                  setFormValidations(prev => ({
                    ...prev,
                    [data.formId]: validations,
                  }));
                }
              });

              // E também com delay para garantir
              setTimeout(() => {
                if (mountedRef.current) {
                  loadFormProgressStats(formIds, true);
                }
              }, 1000);
            }
            break;
          case 'response_validated':
            // Recarregar validações quando uma resposta for validada
            if (data.formId) {
              console.log(
                `🔄 Resposta validada - atualizando validações para formulário ${data.formId}`
              );

              // Forçar recarga imediata
              getFormValidations(data.formId).then(validations => {
                if (mountedRef.current) {
                  setFormValidations(prev => ({
                    ...prev,
                    [data.formId]: validations,
                  }));
                  console.log(`✅ Validações atualizadas para ${data.formId}:`, validations);

                  // Forçar re-render
                  setForceUpdateTrigger(prev => prev + 1);
                }
              });

              // Também recarregar todas as validações após um delay
              setTimeout(async () => {
                if (mountedRef.current) {
                  const formIds = new Set(allForms.map(f => f.id));
                  const allValidations = {};

                  for (const fId of formIds) {
                    const vals = await getFormValidations(fId);
                    if (vals && vals.length > 0) {
                      allValidations[fId] = vals;
                    }
                  }

                  setFormValidations(allValidations);
                }
              }, 1000);
            }
            break;

          // MUDANÇA 3: ADICIONAR novo caso no switch para form_stats_updated
          case 'form_stats_updated':
            if (data.formId) {
              setTimeout(async () => {
                if (mountedRef.current) {
                  const formIds = new Set([data.formId]);
                  await loadFormProgressStats(formIds, true);
                  await loadFormSyncStatus(formIds);
                }
              }, 500);
            }
            break;

          case 'form_saved':
          case 'form_removed':
          case 'forms_updated':
            // Recarregar dados com throttle
            if (loadDataTimeoutRef.current) {
              clearTimeout(loadDataTimeoutRef.current);
            }
            loadDataTimeoutRef.current = setTimeout(() => {
              if (!isLoadingRef.current && mountedRef.current) {
                loadAllData();
              }
            }, DEBOUNCE_DELAY);
            break;

          default:
            // Ignorar outros tipos
            break;
        }
      },
      [loadFormProgressStats, loadFormSyncStatus]
    ),
    THROTTLE_DELAY
  );

  // Função para limpar dados antigos do AsyncStorage
  // Função para limpar dados antigos do AsyncStorage
  const clearOldAsyncStorageData = async () => {
    try {
      console.log('🧹 Limpando dados antigos do AsyncStorage...');

      // Limpar TUDO relacionado a cache primeiro
      const keys = await AsyncStorage.getAllKeys();
      const keysToRemove = keys.filter(
        key =>
          key.startsWith('cached_response_') ||
          key.startsWith('cached_responses_') ||
          key.startsWith('cached_form_') ||
          key.startsWith('form_responses_temp') ||
          key.startsWith('responses_backup') ||
          key.startsWith('forms_backup') ||
          key.startsWith('temp_') ||
          key.includes('_backup') ||
          key.includes('_old') ||
          key.includes('_temp')
      );

      if (keysToRemove.length > 0) {
        await AsyncStorage.multiRemove(keysToRemove);
        console.log(`✅ Removidas ${keysToRemove.length} chaves antigas do cache`);
      }

      // Também limpar dados grandes e não essenciais
      try {
        // Remover backups antigos
        await AsyncStorage.removeItem('deleted_forms');
        await AsyncStorage.removeItem('deleted_responses');
        await AsyncStorage.removeItem('backup_downloaded_ids');
        console.log('✅ Backups antigos removidos');
      } catch (e) {
        // Ignorar se não existirem
      }

      return true;
    } catch (error) {
      console.error('❌ Erro ao limpar AsyncStorage:', error);
      return false;
    }
  };

  // Verificar conexão com o servidor
  const checkServerConnection = useCallback(async () => {
    try {
      const health = await api.checkServerHealth();
      const isOnline = health && health.status === 'online';

      if (mountedRef.current) {
        setIsServerOnline(isOnline);

        if (isOnline) {
          setServerError(null);
        } else {
          setServerError(health?.error || 'Servidor não está respondendo');
        }
      }

      return isOnline;
    } catch (error) {
      console.error('❌ Erro ao verificar conexão:', error);
      if (mountedRef.current) {
        setIsServerOnline(false);
        setServerError(error.message || 'Erro ao verificar conexão');
      }
      return false;
    }
  }, []);

  // FUNÇÃO COMPLETAMENTE CORRIGIDA: Carregamento de dados SEM loops
  const loadAllData = useCallback(async () => {
    if (isLoadingRef.current || !mountedRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setLoading(true);

    try {
      console.log('🔄 Carregando dados...');

      // Verificar conexão com servidor
      const isOnline = await checkServerConnection();

      // Carregar dados locais
      const [localForms, responses] = await Promise.all([
        OptimizedStorageManager.loadCustomForms(),
        OptimizedStorageManager.loadFormResponses(),
      ]);

      let serverForms = [];

      // Tentar carregar dados do servidor se online
      if (isOnline) {
        try {
          serverForms = await api.getForms();
        } catch (serverError) {
          console.warn('⚠️ Erro ao carregar dados do servidor:', serverError);
        }
      }

      // Atualizar cache e estado
      if (mountedRef.current) {
        setCachedData({
          customForms: localForms,
          serverForms: serverForms,
          formResponses: responses,
          downloadedFormIds: new Set(localForms.map(f => f.id)),
        });

        updateAllForms(localForms, serverForms);

        // MUDANÇA 4: ADICIONAR carregamento de status de sincronização
        // Forçar carregamento inicial de todas as estatísticas
        const allFormIds = new Set(localForms.map(f => f.id));
        if (allFormIds.size > 0) {
          await loadFormProgressStats(allFormIds, true);
          // Carregar validações para todos os formulários
          const validations = {};
          for (const formId of allFormIds) {
            validations[formId] = await getFormValidations(formId);
          }
          if (mountedRef.current) {
            setFormValidations(validations);
            console.log('🎯 Estado formValidations atualizado:', validations);
          }
          await loadFormSyncStatus(allFormIds); // ADICIONAR ESTA LINHA
        }
      }

      console.log('✅ Dados carregados com sucesso');
      // Carregar validações após os dados principais
      if (mountedRef.current && localForms.length > 0) {
        console.log('🔄 Carregando validações para todos os formulários...');
        const allFormIds = new Set(localForms.map(f => f.id));

        // Carregar validações independente de estar online ou offline
        const validations = {};
        for (const formId of allFormIds) {
          try {
            const formValidations = await getFormValidations(formId);
            if (formValidations && formValidations.length > 0) {
              validations[formId] = formValidations;
              console.log(`✅ ${formValidations.length} validações encontradas para ${formId}`);
            } else {
              // Buscar localmente também
              const localResponses = await StorageManager.getResponsesByFormId(formId);
              const localValidated = localResponses.filter(
                r => r.validacao && r.validacao.validadoPor
              );
              if (localValidated.length > 0) {
                validations[formId] = localValidated.map(response => ({
                  avaliadorId: response.avaliador?.id,
                  avaliadorNome: response.avaliador?.nome,
                  validadoPor: response.validacao.validadoPor?.nome,
                  dataValidacao: response.validacao.dataValidacao,
                }));
                console.log(
                  `✅ ${localValidated.length} validações locais encontradas para ${formId}`
                );
              }
            }
          } catch (error) {
            console.error(`❌ Erro ao carregar validações para ${formId}:`, error);
          }
        }

        // Carregar validações após os dados principais
        if (mountedRef.current) {
          console.log('🔄 Carregando validações para todos os formulários...');

          // Incluir formulários do servidor também
          const allFormIds = new Set([...localForms.map(f => f.id), ...serverForms.map(f => f.id)]);

          // Carregar validações independente de estar online ou offline
          const validations = {};
          for (const formId of allFormIds) {
            try {
              const formValidations = await getFormValidations(formId);
              if (formValidations && formValidations.length > 0) {
                validations[formId] = formValidations;
                console.log(`✅ ${formValidations.length} validações encontradas para ${formId}`);
              }
            } catch (error) {
              console.error(`❌ Erro ao carregar validações para ${formId}:`, error);
            }
          }

          if (mountedRef.current) {
            setFormValidations(validations);
            console.log('🎯 Todas validações carregadas:', validations);
            // Forçar re-render após carregar validações
            setForceUpdateTrigger(prev => prev + 1);
          }
        }
      }

      // FORÇAR carregamento de estatísticas 2 vezes para garantir
      if (mountedRef.current && localForms.length > 0) {
        const allFormIds = new Set(localForms.map(f => f.id));

        // Primeira vez
        await loadFormProgressStats(allFormIds, true);

        // Segunda vez após delay
        setTimeout(() => {
          if (mountedRef.current) {
            loadFormProgressStats(allFormIds, true);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      if (mountedRef.current) {
        showSnackbar('Erro ao carregar roteiros: ' + (error.message || 'Erro desconhecido'));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      isLoadingRef.current = false;
    }
  }, [updateAllForms, loadFormProgressStats, loadFormSyncStatus, checkServerConnection]);

  // TEMPORÁRIO: Debug das validações
  useEffect(() => {
    console.log('🔄 Estado formValidations mudou:', formValidations);
  }, [formValidations]);
  // EFEITO DE INICIALIZAÇÃO: Executa apenas uma vez
  useEffect(() => {
    mountedRef.current = true;

    const initialize = async () => {
      try {
        console.log('🔄 Inicializando FormListScreen...');

        // Configurar StorageManager otimizado
        OptimizedStorageManager.setLogLevel('minimal');
        await OptimizedStorageManager.initialize();

        // Verificar espaço disponível e limpar preventivamente
        try {
          const spaceInfo = await OptimizedStorageManager.checkAvailableSpace();
          console.log(`📊 Uso de armazenamento: ${spaceInfo.percentUsed}%`);

          // Se estiver acima de 80%, limpar automaticamente
          if (spaceInfo.percentUsed > 80) {
            console.log('⚠️ Armazenamento acima de 80%, limpando preventivamente...');
            await clearOldAsyncStorageData();
            await OptimizedStorageManager.cleanupStorage();
          }
        } catch (error) {
          console.warn('⚠️ Erro ao verificar espaço:', error);
        }
        // Limpar cache antigo se necessário
        try {
          const keys = await AsyncStorage.getAllKeys();
          if (keys.length > 100) {
            // Se tem muitas chaves, limpar
            console.log(`⚠️ AsyncStorage com ${keys.length} chaves, limpando dados antigos...`);
            await clearOldAsyncStorageData();
          }
        } catch (error) {
          console.warn('⚠️ Erro ao verificar AsyncStorage:', error);
        }
        // Configurar listener
        const removeListener = OptimizedStorageManager.addChangeListener(handleStorageChange);
        storageListenerRef.current = removeListener;

        // MUDANÇA 9: ADICIONAR listener específico para atualizações de formulário
        // Adicionar listener específico para atualizações de formulário
        const formUpdateListener = (changeType, data) => {
          if (changeType === 'response_saved' || changeType === 'form_stats_updated') {
            if (data.formId && mountedRef.current) {
              const formIds = new Set([data.formId]);
              loadFormProgressStats(formIds, true);
              loadFormSyncStatus(formIds);
            }
          }
        };

        OptimizedStorageManager.addChangeListener(formUpdateListener);

        // CORREÇÃO: Aguardar um tempo antes de carregar dados para evitar corrida
        setTimeout(async () => {
          if (mountedRef.current) {
            await loadAllData();
          }
        }, 100);

        console.log('✅ FormListScreen inicializado');
      } catch (error) {
        console.error('❌ Erro ao inicializar:', error);
        if (mountedRef.current) {
          showSnackbar('Erro ao inicializar: ' + (error.message || 'Erro desconhecido'));
        }
      }
    };

    initialize();

    // Cleanup
    return () => {
      console.log('🧹 Limpando FormListScreen...');
      mountedRef.current = false;

      if (storageListenerRef.current) {
        storageListenerRef.current();
      }
      if (loadDataTimeoutRef.current) {
        clearTimeout(loadDataTimeoutRef.current);
      }
      isLoadingRef.current = false;

      // Limpar queue de salvamento otimizado
      optimizedSaveRef.current = {
        lastSaveTime: 0,
        pendingSaves: new Map(),
        saveQueue: [],
      };
    };
  }, []); // CORREÇÃO: Dependências vazias

  // CORREÇÃO 2: Efeito para atualizar dados quando retornar de ResponsesScreen
  // Efeito combinado para atualizar dados ao voltar de qualquer tela
  useEffect(() => {
    if (!navigation) {
      console.log('🔍 Navigation não disponível no FormListScreen');
      return;
    }

    const handleFocus = async () => {
      console.log('🔄 Tela focada - forçando atualização completa...');

      try {
        if (mountedRef.current && !isLoadingRef.current) {
          // 1. Recarregar TODAS as respostas primeiro

          // 1.5 Recarregar validações também
          console.log('🔄 Recarregando validações...');
          const allValidations = {};
          for (const form of allForms) {
            try {
              const vals = await getFormValidations(form.id);
              if (vals && vals.length > 0) {
                allValidations[form.id] = vals;
                console.log(`✅ ${vals.length} validações encontradas para ${form.id}`);
              }
            } catch (error) {
              console.error(`❌ Erro ao recarregar validações para ${form.id}:`, error);
            }
          }
          if (mountedRef.current) {
            setFormValidations(allValidations);
            console.log('✅ Todas validações recarregadas:', allValidations);
          }
          await StorageManager.initialize();
          // Forçar recarga de todas as validações ao focar na tela
          console.log('🔄 Recarregando validações de todos os formulários...');
          for (const form of allForms) {
            await forceReloadFormValidations(form.id);
          }

          // Forçar recarga de validações ao retornar para a tela
          // Forçar recarga de validações ao retornar para a tela
          console.log('🔄 Recarregando validações ao focar tela...');
          const formIds = allForms.map(f => f.id);
          for (const formId of formIds) {
            try {
              const vals = await getFormValidations(formId);
              if (vals && vals.length > 0) {
                setFormValidations(prev => ({
                  ...prev,
                  [formId]: vals,
                }));
              }
            } catch (error) {
              console.error(`❌ Erro ao recarregar validações para ${formId}:`, error);
            }
          }
          // 2. Forçar recalculo de TODAS as estatísticas
          //const formIds = new Set(allForms.map(f => f.id));

          // Executar imediatamente
          await loadFormProgressStats(formIds, true);
          // Recarregar validações também
          const validations = {};
          for (const formId of formIds) {
            validations[formId] = await getFormValidations(formId);
          }
          if (mountedRef.current) {
            setFormValidations(validations);
          }

          // E novamente após delay
          setTimeout(async () => {
            if (mountedRef.current) {
              await loadFormProgressStats(formIds, true);
              console.log('✅ Estatísticas atualizadas após retorno');
            }
          }, 1500);
        }
      } catch (error) {
        console.error('❌ Erro ao recarregar após retorno:', error);
      }
    };
    // Adiciona o listener
    const unsubscribe = navigation.addListener('focus', handleFocus);

    return () => {
      console.log('🧹 Removendo listener de focus');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [
    navigation,
    loadAllData,
    loadFormProgressStats,
    loadFormSyncStatus,
    allForms,
    checkServerConnection,
  ]);

  // FUNÇÃO DE REFRESH CORRIGIDA: Com controle de múltiplas execuções
  const onRefresh = useCallback(async () => {
    if (refreshing || isLoadingRef.current || !mountedRef.current) return;

    setRefreshing(true);

    try {
      await loadAllData();
      if (mountedRef.current) {
        showSnackbar('Roteiros atualizados com sucesso!');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar dados:', error);
      if (mountedRef.current) {
        showSnackbar('Erro ao atualizar roteiros: ' + (error.message || 'Erro desconhecido'));
      }
    } finally {
      if (mountedRef.current) {
        setRefreshing(false);
      }
    }
  }, [refreshing, loadAllData]);

  // Mostrar snackbar
  const showSnackbar = useCallback(message => {
    if (mountedRef.current) {
      setSnackbarMessage(message);
      setSnackbarVisible(true);
    }
  }, []);

  // Função genérica para mostrar dialogs
  const showDialog = useCallback((title, message, actions = []) => {
    if (!mountedRef.current) return;

    const defaultActions = [
      { text: 'Cancelar', action: () => setDialogVisible(false) },
      { text: 'Confirmar', action: () => setDialogVisible(false) },
    ];

    setDialogContent({
      title,
      message,
      actions: actions.length > 0 ? actions : defaultActions,
    });
    setDialogVisible(true);
  }, []);

  // NOVA FUNÇÃO: Deletar formulário permanentemente (apenas para programador/coordenação)
  const deleteFormPermanently = useCallback(
    async (formId, formTitle) => {
      const effectiveUser = isSimulating && simulatedUser ? simulatedUser : currentUser;

      // Verificar permissão
      if (
        !effectiveUser ||
        (effectiveUser.level !== 'programador' && effectiveUser.level !== 'coordenacao')
      ) {
        showSnackbar('Você não tem permissão para excluir formulários');
        return;
      }

      showDialog(
        '🗑️ ATENÇÃO: Exclusão Permanente',
        `⚠️ ESTA AÇÃO É IRREVERSÍVEL!\n\n` +
          `Você está prestes a excluir PERMANENTEMENTE:\n\n` +
          `📋 Formulário: "${formTitle}"\n` +
          `📝 Todas as respostas associadas\n` +
          `📎 Todos os anexos\n\n` +
          `Esta ação removerá o formulário tanto do servidor quanto de todos os dispositivos.\n\n` +
          `Tem certeza ABSOLUTA que deseja continuar?`,
        [
          { text: '❌ Cancelar', action: () => setDialogVisible(false) },
          {
            text: '🗑️ SIM, EXCLUIR PERMANENTEMENTE',
            action: async () => {
              try {
                setLoading(true);

                // Verificar conexão com servidor
                const isOnline = await checkServerConnection();

                if (!isOnline) {
                  showSnackbar('Servidor offline. Não é possível excluir formulários sem conexão.');
                  return;
                }

                // Excluir do servidor (versão completa que deleta tudo)
                await api.deleteFormComplete(formId);
                console.log(`✅ Formulário ${formId} excluído completamente do servidor`);

                // Remover localmente
                await OptimizedStorageManager.removeForm(formId);

                // Remover todas as respostas relacionadas
                const updatedResponses = cachedData.formResponses.filter(
                  response => response.formId !== formId
                );
                await OptimizedStorageManager.saveFormResponses(updatedResponses);
                setFormProgressStats(prev => {
                  const newStats = { ...prev };
                  delete newStats[formId];
                  return newStats;
                });

                // Recarregar formulários
                await loadAllData();

                showDialog(
                  '✅ Exclusão Concluída',
                  `O formulário "${formTitle}" e todas as suas respostas foram excluídos permanentemente do sistema.`,
                  [{ text: 'OK', action: () => setDialogVisible(false) }]
                );
              } catch (error) {
                console.error('❌ Erro ao excluir formulário:', error);
                showDialog(
                  '❌ Erro na Exclusão',
                  'Falha ao excluir formulário: ' + (error.message || 'Erro desconhecido'),
                  [{ text: 'OK', action: () => setDialogVisible(false) }]
                );
              } finally {
                if (mountedRef.current) {
                  setLoading(false);
                }
              }
            },
          },
        ]
      );
    },
    [
      isSimulating,
      simulatedUser,
      currentUser,
      showDialog,
      showSnackbar,
      checkServerConnection,
      cachedData.formResponses,
      loadAllData,
    ]
  );

  // MUDANÇA 5: MODIFICAR handleEditAvaliador para recarregar stats
  const handleEditAvaliador = useCallback(
    async (formId, position, newAvaliador) => {
      try {
        setLoading(true);

        // Buscar o formulário atual
        const form = allForms.find(f => f.id === formId);
        if (!form) {
          throw new Error('Formulário não encontrado');
        }

        // Preparar os novos metadados
        const updatedMetadata = { ...form.metadata };

        if (position === 1) {
          updatedMetadata.avaliador1 = newAvaliador.id;
        } else {
          updatedMetadata.avaliador2 = newAvaliador.id;
        }

        // Atualizar o campo author com os nomes concatenados
        const avaliador1Nome =
          position === 1
            ? newAvaliador.nome
            : AVALIADORES.find(a => a.id === updatedMetadata.avaliador1)?.nome || '';
        const avaliador2Nome =
          position === 2
            ? newAvaliador.nome
            : AVALIADORES.find(a => a.id === updatedMetadata.avaliador2)?.nome || '';

        const updatedAuthor = `${avaliador1Nome} & ${avaliador2Nome}`;

        // Verificar conexão
        const isOnline = await checkServerConnection();

        if (isOnline) {
          // Atualizar no servidor usando a função específica da API
          await api.updateFormEvaluators(
            formId,
            updatedMetadata.avaliador1,
            updatedMetadata.avaliador2
          );
          console.log('✅ Avaliadores atualizados no servidor');
        } else {
          showSnackbar('⚠️ Servidor offline. A alteração será salva localmente.');
        }

        // Atualizar localmente
        const updatedForm = {
          ...form,
          metadata: updatedMetadata,
          author: updatedAuthor,
          updatedAt: new Date().toISOString(),
        };

        await OptimizedStorageManager.saveForm(updatedForm);

        // Recarregar dados
        await loadAllData();

        // MUDANÇA 5: Recarregar estatísticas do formulário
        setTimeout(async () => {
          if (mountedRef.current) {
            const formIds = new Set([formId]);
            await loadFormProgressStats(formIds, true);
          }
        }, 1000);

        showSnackbar(`Avaliador ${position} alterado para ${newAvaliador.nome}`);
      } catch (error) {
        console.error('❌ Erro ao editar avaliador:', error);
        showSnackbar('Erro ao alterar avaliador: ' + (error.message || 'Erro desconhecido'));
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [allForms, checkServerConnection, showSnackbar, loadAllData, loadFormProgressStats]
  );

  // NOVA FUNÇÃO: Editar emails das lideranças
  const handleEditEmails = useCallback(
    async (formId, newEmails) => {
      try {
        setLoading(true);

        // Buscar o formulário atual
        const form = allForms.find(f => f.id === formId);
        if (!form) {
          throw new Error('Formulário não encontrado');
        }

        // Verificar conexão
        const isOnline = await checkServerConnection();

        if (!isOnline) {
          showSnackbar('⚠️ Servidor offline. Não é possível editar emails sem conexão.');
          return;
        }

        // Preparar dados para atualização
        const updateData = {
          ...form,
          metadata: {
            ...form.metadata,
            emailsLiderancas: newEmails,
          },
          updatedAt: new Date().toISOString(),
        };

        // Atualizar no servidor
        await api.updateForm(formId, updateData);
        console.log('✅ Emails das lideranças atualizados no servidor');

        // Atualizar localmente
        await OptimizedStorageManager.saveForm(updateData);

        // Recarregar dados
        await loadAllData();

        showSnackbar(`✅ Emails das lideranças atualizados com sucesso`);
      } catch (error) {
        console.error('❌ Erro ao editar emails:', error);
        showSnackbar('Erro ao alterar emails: ' + (error.message || 'Erro desconhecido'));
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [allForms, checkServerConnection, showSnackbar, loadAllData]
  );

  // NOVA FUNÇÃO: Enviar email para lideranças
  const handleSendLeadershipEmail = useCallback(
    async (formId, customMessage, sentBy) => {
      try {
        setLoading(true);

        // Verificar conexão
        const isOnline = await checkServerConnection();

        if (!isOnline) {
          showSnackbar('⚠️ Servidor offline. Não é possível enviar emails sem conexão.');
          return;
        }

        // Enviar email
        await api.sendLeadershipEmail(formId, customMessage, sentBy);

        // Recarregar dados para atualizar status
        await loadAllData();

        showSnackbar('✅ Email enviado com sucesso para as lideranças!');
      } catch (error) {
        console.error('❌ Erro ao enviar email:', error);
        showSnackbar('Erro ao enviar email: ' + (error.message || 'Erro desconhecido'));
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [checkServerConnection, showSnackbar, loadAllData]
  );

  // Função para abrir modal de edição genérico
  const openEditModal = useCallback((type, formId, currentValue) => {
    setEditModalType(type);
    setEditModalFormId(formId);
    setEditModalValue(currentValue || '');
    setEditModalError('');
    setEditModalVisible(true);
  }, []);

  // Função para salvar edição genérica
  const handleSaveEdit = useCallback(async () => {
    try {
      // Validações específicas por tipo
      if (editModalType === 'date' && !validateDateFormat(editModalValue)) {
        setEditModalError('Data inválida. Use o formato DD/MM/AAAA');
        return;
      }

      if (editModalType === 'time' && !validateTimeFormat(editModalValue)) {
        setEditModalError('Horário inválido. Use o formato HH:MM');
        return;
      }

      if (!editModalValue.trim()) {
        setEditModalError('Este campo não pode estar vazio');
        return;
      }

      setLoading(true);

      // Buscar formulário atual
      const form = allForms.find(f => f.id === editModalFormId);
      if (!form) {
        throw new Error('Formulário não encontrado');
      }

      // Preparar dados de atualização
      const updateData = { ...form };

      switch (editModalType) {
        case 'title':
          updateData.title = editModalValue.trim();
          break;
        case 'description':
          updateData.description = editModalValue.trim();
          break;
        case 'date':
          updateData.metadata.dataAvaliacao = editModalValue;
          break;
        case 'time':
          updateData.metadata.horarioAvaliacao = editModalValue;
          break;
        case 'location':
          updateData.metadata.localAvaliacao = editModalValue.trim();
          break;
        case 'contact':
          updateData.metadata.contatoAvaliacao = editModalValue.trim();
          break;
      }

      // Verificar conexão
      const isOnline = await checkServerConnection();
      if (!isOnline) {
        showSnackbar('⚠️ Servidor offline. Não é possível editar sem conexão.');
        return;
      }

      // Atualizar no servidor
      await api.updateFormMetadata(editModalFormId, updateData);

      // Atualizar localmente
      await OptimizedStorageManager.saveForm(updateData);

      // Recarregar dados
      await loadAllData();

      // Fechar modal
      setEditModalVisible(false);
      showSnackbar('✅ Campo atualizado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao salvar edição:', error);
      setEditModalError('Erro ao salvar: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, [
    editModalType,
    editModalFormId,
    editModalValue,
    allForms,
    checkServerConnection,
    showSnackbar,
    loadAllData,
  ]);

  // Validadores de formato

  const validateDateFormat = dateString => {
    if (!dateString) return false;
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateString)) return false;

    const [, day, month, year] = dateString.match(regex);
    const date = new Date(year, month - 1, day);
    return date.getDate() == day && date.getMonth() == month - 1 && date.getFullYear() == year;
  };

  const validateTimeFormat = timeString => {
    if (!timeString) return false;
    const regex = /^(\d{2}):(\d{2})$/;
    if (!regex.test(timeString)) return false;

    const [, hour, minute] = timeString.match(regex);
    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  };

  // Funções para edição de Unidades Organizacionais
  const openUnidadeEditModal = useCallback(
    async formId => {
      const form = allForms.find(f => f.id === formId);
      if (!form || !form.metadata) return;

      setUnidadeEditFormId(formId);
      setUnidadeEditSelectedUnidades(form.metadata.unidadesOrganizacionais || []);
      setUnidadeEditSearchText('');
      setUnidadeEditFilteredUnidades([]);
      setUnidadeEditShowDropdown(false);
      setUnidadeEditModalVisible(true);
    },
    [allForms]
  );

  const handleSaveUnidadesEdit = useCallback(async () => {
    try {
      if (unidadeEditSelectedUnidades.length === 0) {
        showSnackbar('É necessário ter pelo menos uma unidade organizacional');
        return;
      }

      setLoading(true);

      const form = allForms.find(f => f.id === unidadeEditFormId);
      if (!form) {
        throw new Error('Formulário não encontrado');
      }

      const updateData = {
        ...form,
        metadata: {
          ...form.metadata,
          unidadesOrganizacionais: unidadeEditSelectedUnidades,
        },
      };

      const isOnline = await checkServerConnection();
      if (!isOnline) {
        showSnackbar('⚠️ Servidor offline. Não é possível editar sem conexão.');
        return;
      }

      await api.updateFormMetadata(unidadeEditFormId, updateData);
      await OptimizedStorageManager.saveForm(updateData);
      await loadAllData();

      setUnidadeEditModalVisible(false);
      showSnackbar('✅ Unidades organizacionais atualizadas com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao salvar unidades:', error);
      showSnackbar('Erro ao salvar: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, [
    unidadeEditFormId,
    unidadeEditSelectedUnidades,
    allForms,
    checkServerConnection,
    showSnackbar,
    loadAllData,
  ]);

  // Carregar processos únicos
  const loadUniqueProcessosForEdit = useCallback(async () => {
    try {
      const processosMap = new Map();

      allForms.forEach(form => {
        if (form.metadata?.processosAvaliados) {
          form.metadata.processosAvaliados.forEach(processo => {
            if (!processosMap.has(processo.id)) {
              processosMap.set(processo.id, processo);
            }
          });
        } else if (form.metadata?.processoAvaliado) {
          const processo = form.metadata.processoAvaliado;
          if (!processosMap.has(processo.id)) {
            processosMap.set(processo.id, processo);
          }
        }
      });

      const processosArray = Array.from(processosMap.values()).sort((a, b) =>
        a.nome.localeCompare(b.nome, 'pt-BR')
      );

      setProcessoEditAllProcessos(processosArray);
    } catch (error) {
      console.error('❌ Erro ao carregar processos:', error);
    }
  }, [allForms]);

  // Abrir modal de edição de processos
  const openProcessoEditModal = useCallback(
    async formId => {
      const form = allForms.find(f => f.id === formId);
      if (!form || !form.metadata) return;

      await loadUniqueProcessosForEdit();

      setProcessoEditFormId(formId);
      setProcessoEditSelectedProcessos(
        form.metadata.processosAvaliados ||
          (form.metadata.processoAvaliado ? [form.metadata.processoAvaliado] : [])
      );
      setProcessoEditSearchText('');
      setProcessoEditFilteredProcessos([]);
      setProcessoEditShowDropdown(false);
      setProcessoEditModalVisible(true);
    },
    [allForms, loadUniqueProcessosForEdit]
  );

  // Salvar processos editados
  const handleSaveProcessosEdit = useCallback(async () => {
    try {
      if (processoEditSelectedProcessos.length === 0) {
        showSnackbar('É necessário ter pelo menos um processo avaliado');
        return;
      }

      setLoading(true);

      const form = allForms.find(f => f.id === processoEditFormId);
      if (!form) {
        throw new Error('Formulário não encontrado');
      }

      const updateData = {
        ...form,
        metadata: {
          ...form.metadata,
          processosAvaliados: processoEditSelectedProcessos,
          processoAvaliado: processoEditSelectedProcessos[0], // Compatibilidade
        },
      };

      const isOnline = await checkServerConnection();
      if (!isOnline) {
        showSnackbar('⚠️ Servidor offline. Não é possível editar sem conexão.');
        return;
      }

      await api.updateFormMetadata(processoEditFormId, updateData);
      await OptimizedStorageManager.saveForm(updateData);
      await loadAllData();

      setProcessoEditModalVisible(false);
      showSnackbar('✅ Processos avaliados atualizados com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao salvar processos:', error);
      showSnackbar('Erro ao salvar: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, [
    processoEditFormId,
    processoEditSelectedProcessos,
    allForms,
    checkServerConnection,
    showSnackbar,
    loadAllData,
  ]);

  // Função para abrir modal de email
  const openEmailModal = useCallback(formId => {
    setEmailModalFormId(formId);
    setEmailModalVisible(true);
  }, []);

  // MUDANÇA 6: SUBSTITUIR completamente a função getActionButton
  const getActionButton = useCallback(
    (form, progressStats) => {
      const progress = progressStats?.progress?.percentage || 0;
      const isDownloaded =
        cachedData.downloadedFormIds.has(form.id) ||
        cachedData.customForms.some(f => f.id === form.id);
      const syncStatus = formSyncStatus[form.id];
      const hasResponses = progressStats?.hasResponses || false;

      // Se não está baixado, mostrar botão de download
      if (!isDownloaded) {
        return (
          <View style={styles.buttonRow}>
            <Button
              mode="contained"
              onPress={() => downloadFormFromServer(form)}
              icon="download"
              style={styles.downloadButton}
              contentStyle={styles.buttonContent}
              labelStyle={styles.buttonLabel}
              buttonColor={AVAQualisTheme.colors.info}
              loading={downloadingFormId === form.id}
              disabled={downloadingFormId === form.id}
            >
              Baixar
            </Button>
          </View>
        );
      }

      // Verificar status de sincronização
      const isCloudNewer = syncStatus?.status === 'cloud-newer';
      const isLocalNewer = syncStatus?.status === 'local-newer';
      const isSynced = syncStatus?.status === 'synced';

      // Se versão da nuvem é mais recente, mostrar botão de atualização
      if (isCloudNewer && isServerOnline) {
        return (
          <View style={styles.buttonRow}>
            <IconButton
              icon="delete-outline"
              iconColor={AVAQualisTheme.colors.error}
              style={styles.deleteIconButton}
              onPress={() => deleteForm(form.id, form.title)}
            />
            <Button
              mode="contained"
              onPress={() => handleUpdateFromCloud(form)}
              icon="cloud-download"
              style={styles.downloadButton}
              contentStyle={styles.buttonContent}
              labelStyle={styles.buttonLabel}
              buttonColor="#D32F2F"
            >
              📥 Baixar Versão Atualizada
            </Button>
          </View>
        );
      }

      // Se tem respostas e está em progresso
      // Se tem respostas e está em progresso
      if (hasResponses && progress > 0 && progress < 100) {
        return (
          <View style={styles.buttonRow}>
            <IconButton
              icon="delete-outline"
              iconColor={AVAQualisTheme.colors.error}
              style={styles.deleteIconButton}
              onPress={() => deleteForm(form.id, form.title)}
            />
            <Button
              mode="contained"
              onPress={() => {
                // USAR A MESMA LÓGICA DO EDITAR CÓPIA DO ResponsesScreen
                const formCopy = {
                  id: form.id,
                  title: form.title,
                  description: form.description,
                  version: form.version,
                  requirements: form.requirements || [],
                  questions: form.questions || [],
                  totalQuestions:
                    form.totalQuestions || (form.requirements ? form.requirements.length : 0),
                  estimatedTime: form.estimatedTime,
                  metadata: form.metadata,
                  // FLAGS IMPORTANTES
                  isEditingFromResults: true, // IMPORTANTE: mesma flag do ResponsesScreen
                  isFromResponseScreen: true, // ADICIONAR esta flag
                  currentProgress: progress,
                  hasExistingResponses: true,
                  existingResponseId: progressStats?.responseId, // ID da resposta existente
                };
                onSelectForm(formCopy);
              }}
              icon="pencil"
              style={styles.continueButton}
              contentStyle={styles.buttonContent}
              labelStyle={styles.buttonLabel}
              buttonColor={AVAQualisTheme.colors.warning}
            >
              Continuar ({progress}%)
            </Button>
            {/* resto do código... */}
          </View>
        );
      }

      // Se está 100% completo
      if (progress === 100) {
        return (
          <View style={styles.buttonRow}>
            <IconButton
              icon="delete-outline"
              iconColor={AVAQualisTheme.colors.tertiary}
              style={styles.deleteIconButton}
              onPress={() => deleteForm(form.id, form.title)}
            />
            {isSynced ? (
              <Button
                mode="outlined"
                onPress={() => {
                  const formCopy = {
                    ...form,
                    requirements: form.requirements || [],
                    totalQuestions:
                      form.totalQuestions || (form.requirements ? form.requirements.length : 0),
                    isEditingFromResults: true,
                    isViewOnly: true,
                  };
                  onSelectForm(formCopy);
                }}
                icon="eye"
                style={styles.viewButton}
                contentStyle={styles.buttonContent}
                labelStyle={styles.viewButtonLabel}
                textColor={AVAQualisTheme.colors.success}
              >
                ✅ Visualizar (Sincronizado)
              </Button>
            ) : (
              <Button
                mode="contained"
                onPress={() => onViewResponses()}
                icon="cloud-upload"
                style={styles.syncButton}
                contentStyle={styles.buttonContent}
                labelStyle={[styles.buttonLabel, { color: AVAQualisTheme.colors.onPrimary }]}
                buttonColor={AVAQualisTheme.colors.primary}
              >
                📤 100% Enviado - 📁 Pasta de Uploads
              </Button>
            )}
            {isLocalNewer && isServerOnline && (
              <IconButton
                icon="cloud-upload"
                iconColor="#FF6F00"
                size={20}
                onPress={() => handleSyncToCloud(form.id)}
                style={styles.syncIconButton}
              />
            )}
          </View>
        );
      }

      // Para formulários baixados sem respostas
      return (
        <View style={styles.buttonRow}>
          <IconButton
            icon="delete-outline"
            iconColor={AVAQualisTheme.colors.error}
            style={styles.deleteIconButton}
            onPress={() => deleteForm(form.id, form.title)}
          />
          <Button
            mode="contained"
            onPress={() => {
              const formCopy = {
                ...form,
                requirements: form.requirements || [],
                totalQuestions:
                  form.totalQuestions || (form.requirements ? form.requirements.length : 0),
                isEditingFromResults: false,
              };
              onSelectForm(formCopy);
            }}
            icon="play"
            style={styles.startButton}
            contentStyle={styles.buttonContent}
            labelStyle={[styles.buttonLabel, { color: AVAQualisTheme.colors.onPrimary }]}
            buttonColor={AVAQualisTheme.colors.success}
          >
            Iniciar
          </Button>
        </View>
      );
    },
    [
      cachedData,
      downloadingFormId,
      formSyncStatus,
      isServerOnline,
      onSelectForm,
      onViewResponses,
      showSnackbar,
    ]
  );

  // MUDANÇA 7: ADICIONAR novas funções após getActionButton
  // NOVA FUNÇÃO: Atualizar do servidor
  const handleUpdateFromCloud = useCallback(
    async form => {
      try {
        setLoading(true);
        showSnackbar('Baixando versão atualizada do servidor...');

        // Baixar versão atualizada
        await downloadFormFromServer(form);

        // Recarregar dados
        await loadAllData();

        showSnackbar('✅ Formulário atualizado com sucesso!');
      } catch (error) {
        console.error('❌ Erro ao atualizar do servidor:', error);
        showSnackbar('Erro ao atualizar formulário: ' + error.message);
      } finally {
        setLoading(false);
      }
    },
    [loadAllData, showSnackbar]
  );

  // NOVA FUNÇÃO: Sincronizar para o servidor
  const handleSyncToCloud = useCallback(
    async formId => {
      try {
        showSnackbar('Sincronizando com o servidor...');

        // Navegar para ResponsesScreen para fazer o upload
        onViewResponses();
      } catch (error) {
        console.error('❌ Erro ao sincronizar:', error);
        showSnackbar('Erro ao sincronizar: ' + error.message);
      }
    },
    [onViewResponses, showSnackbar]
  );

  // MUDANÇA 3: MODIFICAR downloadFormFromServer para forçar recarga das estatísticas
  const downloadFormFromServer = useCallback(
    async form => {
      if (!isServerOnline && !(await checkServerConnection())) {
        showSnackbar('Servidor offline. Não é possível baixar o roteiro.');
        return false;
      }

      try {
        setDownloadingFormId(form.id);
        console.log(`🔄 Baixando formulário: ${form.title}`);

        const serverForm = await api.getFormById(form.id);

        if (!serverForm) {
          throw new Error('Formulário não encontrado no servidor');
        }

        const formattedForm = {
          ...serverForm,
          id: form.id,
          synced: true,
          syncedAt: new Date().toISOString(),
          isDownloaded: true,
          requirements: serverForm.requirements || [],
        };

        const saveResult = await OptimizedStorageManager.saveForm(formattedForm);

        if (!saveResult) {
          throw new Error('Falha ao salvar o formulário localmente');
        }

        // Recarregar dados
        await loadAllData();

        // MUDANÇA 3: Aguardar um pouco e forçar recarga das estatísticas
        setTimeout(async () => {
          if (mountedRef.current) {
            const formIds = new Set([form.id]);
            await loadFormProgressStats(formIds, true); // true = forceReload
            await loadFormSyncStatus(formIds);

            // Forçar mais um update após 2 segundos
            setTimeout(() => {
              if (mountedRef.current) {
                loadFormProgressStats(formIds, true);
                loadFormSyncStatus(formIds);
              }
            }, 2000);
          }
        }, 500);

        showSnackbar(`Roteiro "${form.title}" baixado com sucesso!`);
        console.log(`✅ Download completo: ${form.title}`);

        return true;
      } catch (error) {
        console.error(`❌ Erro ao baixar formulário:`, error);
        showSnackbar('Erro ao baixar roteiro: ' + (error.message || 'Erro desconhecido'));
        return false;
      } finally {
        if (mountedRef.current) {
          setDownloadingFormId(null);
        }
      }
    },
    [
      isServerOnline,
      checkServerConnection,
      loadAllData,
      loadFormProgressStats,
      loadFormSyncStatus,
      showSnackbar,
    ]
  );

  // FUNÇÃO CORRIGIDA: Excluir formulário local (lixeira funcional)
  const deleteForm = useCallback(
    async (formId, formTitle) => {
      const isOnServer = cachedData.serverForms.some(f => f.id === formId);

      const dialogMessage = `Deseja remover "${formTitle}" do seu dispositivo?\n\n${
        isOnServer
          ? 'Este roteiro continuará disponível no servidor para download futuro.'
          : 'Este roteiro foi criado localmente e não está sincronizado com o servidor.'
      }`;

      showDialog('🗑️ Remover Versão Local', dialogMessage, [
        { text: 'Cancelar', action: () => setDialogVisible(false) },
        {
          text: 'Remover',
          action: async () => {
            try {
              setLoading(true);

              // Remover formulário usando StorageManager otimizado
              const formRemoved = await OptimizedStorageManager.removeForm(formId);

              if (!formRemoved) {
                throw new Error('Falha ao remover o formulário');
              }

              // Remover todas as respostas relacionadas do storage local
              const updatedResponses = cachedData.formResponses.filter(
                response => response.formId !== formId
              );
              await OptimizedStorageManager.saveFormResponses(updatedResponses);

              // Remover das estatísticas de progresso
              setFormProgressStats(prev => {
                const newStats = { ...prev };
                delete newStats[formId];
                return newStats;
              });

              // Recarregar dados
              await loadAllData();

              showDialog(
                '✅ Removido',
                `"${formTitle}" foi removido do seu dispositivo. ${
                  isOnServer
                    ? 'Você pode baixá-lo novamente quando precisar.'
                    : 'Note que este roteiro foi removido apenas localmente.'
                }`,
                [{ text: 'OK', action: () => setDialogVisible(false) }]
              );
            } catch (error) {
              showDialog(
                '❌ Erro',
                'Falha ao excluir roteiro: ' + (error.message || 'Erro desconhecido'),
                [{ text: 'OK', action: () => setDialogVisible(false) }]
              );
              console.error('Erro ao excluir:', error);
            } finally {
              if (mountedRef.current) {
                setLoading(false);
              }
            }
          },
        },
      ]);
    },
    [cachedData.serverForms, cachedData.formResponses, loadAllData, showDialog]
  );

  // Função para limpar cache
  const cleanupStorage = useCallback(async () => {
    // LIMPAR DADOS ANTIGOS DE RESPOSTAS PRIMEIRO
    try {
      const keys = await AsyncStorage.getAllKeys();
      const keysToRemove = keys.filter(
        key =>
          key.includes('cached_response') ||
          key.includes('form_responses') ||
          key.includes('responses_backup') ||
          key.includes('_temp')
      );

      if (keysToRemove.length > 0) {
        await AsyncStorage.multiRemove(keysToRemove);
        console.log(`🧹 ${keysToRemove.length} chaves antigas removidas`);
      }
    } catch (cleanError) {
      console.error('Erro ao limpar cache antigo:', cleanError);
    }
    try {
      setDialogVisible(true);
      setDialogContent({
        title: '🧹 Limpar Cache',
        message:
          'Deseja limpar o cache do aplicativo? Isso vai liberar espaço removendo dados temporários. Seus formulários baixados serão preservados.',
        actions: [
          { text: 'Cancelar', action: () => setDialogVisible(false) },
          {
            text: 'Limpar Cache',
            action: async () => {
              try {
                setLoading(true);

                const removedCount = await OptimizedStorageManager.cleanupStorage();

                setDialogVisible(false);
                showSnackbar(`Cache limpo com sucesso! Removidos ${removedCount} itens.`);

                await loadAllData();

                // Forçar recarga de todas as validações
                setTimeout(async () => {
                  if (mountedRef.current) {
                    console.log('🔄 Recarregando validações após limpeza...');
                    const allFormIds = allForms.map(f => f.id);
                    const newValidations = {};

                    for (const formId of allFormIds) {
                      try {
                        const vals = await getFormValidations(formId);
                        if (vals && vals.length > 0) {
                          newValidations[formId] = vals;
                        }
                      } catch (error) {
                        console.error(`❌ Erro ao recarregar validações para ${formId}:`, error);
                      }
                    }

                    setFormValidations(newValidations);
                    setForceUpdateTrigger(prev => prev + 1);
                    console.log('✅ Validações recarregadas com sucesso');
                  }
                }, 1000);
              } catch (error) {
                console.error('❌ Erro ao limpar cache:', error);
                showSnackbar('Erro ao limpar cache: ' + (error.message || 'Erro desconhecido'));
              } finally {
                if (mountedRef.current) {
                  setLoading(false);
                }
              }
            },
          },
        ],
      });
    } catch (error) {
      console.error('❌ Erro ao preparar limpeza:', error);
    }
  }, [loadAllData, showSnackbar]);

  // Formatar data
  const formatDate = useCallback(dateString => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Data inválida';
    }
  }, []);

  // Obter nome do avaliador
  const getAvaliadorNome = useCallback(avaliadorId => {
    if (!avaliadorId) return 'Não informado';
    const avaliador = AVALIADORES.find(a => a.id === avaliadorId);
    return avaliador ? avaliador.nome : 'Não informado';
  }, []);

  // Contar requisitos essenciais
  const countEssentialRequirements = useCallback(form => {
    if (!form || !form.requirements || !Array.isArray(form.requirements)) {
      if (form && form.questions && Array.isArray(form.questions)) {
        return form.questions.filter(q => q.isEssential).length;
      }
      return 0;
    }
    return form.requirements.filter(req => req.isEssential).length;
  }, []);

  // Buscar validações do formulário
  // Buscar validações do formulário
  // Buscar validações do formulário
  const getFormValidations = useCallback(
    async formId => {
      try {
        console.log('🔍 Buscando validações para formulário:', formId);

        let validations = [];

        // SEMPRE buscar do servidor primeiro se estiver online
       if (isServerOnline) {
          try {
            console.log('🔍 Buscando respostas do servidor para validações...');
            // Buscar TODAS as respostas do formulário no servidor
            const serverResponses = await api.getResponsesByFormId(formId);
            console.log(
              `📥 ${serverResponses.length} respostas do servidor para formulário ${formId}`
            );

            // Filtrar apenas as validadas
            const validatedResponses = serverResponses.filter(
              response => response.validacao && response.validacao.validadoPor
            );

            console.log(`✅ ${validatedResponses.length} respostas validadas no servidor`);

            if (validatedResponses.length > 0) {
              // IMPORTANTE: Tentar salvar as respostas validadas localmente
              // IMPORTANTE: Tentar salvar as respostas validadas localmente
              for (const validatedResponse of validatedResponses) {
                try {
                  console.log(`💾 Salvando resposta validada localmente:`, {
                    id: validatedResponse.id,
                    temValidacao: !!validatedResponse.validacao,
                    validadoPor: validatedResponse.validacao?.validadoPor?.nome
                  });
                  
                  // Garantir que a resposta validada seja salva localmente com o campo validacao
                  const responseToSave = {
                    ...validatedResponse,
                    synced: true,
                    syncedAt: validatedResponse.syncedAt || new Date().toISOString(),
                    validacao: validatedResponse.validacao, // GARANTIR que validacao seja preservado
                  };
                  
                  // Verificar se validacao está presente antes de salvar
                  if (!responseToSave.validacao) {
                    console.warn('⚠️ Tentando salvar resposta sem validacao!');
                  }
                  
                  await StorageManager.saveResponse(responseToSave);
                  console.log(`✅ Resposta validada salva com sucesso: ${validatedResponse.id}`);
                } catch (saveError) {
                  // Se for erro de quota, limpar cache automaticamente
                  if (saveError.message && saveError.message.includes('QuotaExceededError')) {
                    console.warn('⚠️ Storage cheio detectado, limpando cache automaticamente...');
                    await clearOldAsyncStorageData();

                    // Tentar salvar novamente após limpar
                    try {
                      await StorageManager.saveResponse({
                        ...validatedResponse,
                        synced: true,
                        syncedAt: validatedResponse.syncedAt || new Date().toISOString(),
                        validacao: validatedResponse.validacao,
                      });
                      console.log(`✅ Resposta validada salva após limpeza: ${validatedResponse.id}`);
                    } catch (retryError) {
                      console.warn('⚠️ Ainda sem espaço após limpeza:', retryError);
                    }
                  } else {
                    console.error('❌ Erro ao salvar resposta validada:', saveError);
                  }
                }
              }

              // Mapear validações
              validations = validatedResponses.map(response => ({
                avaliadorId: response.avaliador?.id,
                avaliadorNome: response.avaliador?.nome,
                validadoPor: response.validacao.validadoPor?.nome,
                dataValidacao: response.validacao.dataValidacao,
              }));
            }
          } catch (error) {
            console.error('❌ Erro ao buscar respostas do servidor:', error);
          }
        }

        // Se não encontrou no servidor ou está offline, buscar localmente
        if (validations.length === 0) {
          try {
            const allResponses = await StorageManager.loadFormResponses();
            const formResponses = allResponses.filter(r => r.formId === formId);

            const validatedResponses = formResponses.filter(
              response => response.validacao && response.validacao.validadoPor
            );

            if (validatedResponses.length > 0) {
              validations = validatedResponses.map(response => ({
                avaliadorId: response.avaliador?.id,
                avaliadorNome: response.avaliador?.nome,
                validadoPor: response.validacao.validadoPor?.nome,
                dataValidacao: response.validacao.dataValidacao,
              }));
              console.log(`✅ ${validatedResponses.length} validações locais encontradas`);
            }
          } catch (localError) {
            console.error('❌ Erro ao buscar validações locais:', localError);
          }
        }

        console.log(`📋 Total de validações para formulário ${formId}:`, validations.length);
        return validations;
      } catch (error) {
        console.error('❌ Erro ao buscar validações:', error);
        return [];
      }
    },
    [isServerOnline]
  );
  // Forçar recarga de validações de um formulário específico
  const forceReloadFormValidations = useCallback(
    async formId => {
      console.log(`🔄 Forçando recarga de validações para formulário ${formId}`);

      try {
        const validations = await getFormValidations(formId);

        if (mountedRef.current) {
          setFormValidations(prev => ({
            ...prev,
            [formId]: validations,
          }));

          // Forçar re-render
          setForceUpdateTrigger(prev => prev + 1);

          console.log(`✅ Validações recarregadas para ${formId}:`, validations);
        }
      } catch (error) {
        console.error('❌ Erro ao forçar recarga de validações:', error);
      }
    },
    [getFormValidations]
  );
  // Obter complexidade do formulário
  const getFormComplexity = useCallback(questionCount => {
    if (!questionCount || isNaN(questionCount)) {
      return { label: 'N/A', color: AVAQualisTheme.colors.tertiary, icon: '❓' };
    }

    if (questionCount <= 8)
      return { label: 'Rápido', color: AVAQualisTheme.colors.success, icon: '⚡' };
    if (questionCount <= 15)
      return { label: 'Médio', color: AVAQualisTheme.colors.warning, icon: '⏱️' };
    return { label: 'Detalhado', color: AVAQualisTheme.colors.error, icon: '📋' };
  }, []);

  // DERIVAR DADOS CALCULADOS USANDO USEMEMO
  const { downloadedForms, availableForms, effectiveUser, canEditAvaliadores } = useMemo(() => {
    const downloaded = allForms.filter(
      form =>
        cachedData.downloadedFormIds.has(form.id) ||
        cachedData.customForms.some(f => f.id === form.id)
    );
    const available = allForms.filter(
      form =>
        !cachedData.downloadedFormIds.has(form.id) &&
        !cachedData.customForms.some(f => f.id === form.id)
    );

    const effective = isSimulating && simulatedUser ? simulatedUser : currentUser;

    // Verificar se pode editar avaliadores
    const canEdit =
      effective && (effective.level === 'programador' || effective.level === 'coordenacao');

    return {
      downloadedForms: downloaded,
      availableForms: available,
      effectiveUser: effective,
      canEditAvaliadores: canEdit,
    };
  }, [allForms, cachedData, isSimulating, simulatedUser, currentUser]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <Appbar.Header style={styles.appbar}>
        <Appbar.Content
          title="📁 Roteiros Disponíveis"
          subtitle={
            effectiveUser?.id === 'visitor'
              ? 'Faça login para acessar seus roteiros de avaliação'
              : effectiveUser?.level === 'membro'
                ? `Roteiros onde ${effectiveUser.nome.split(' ')[0]} é avaliador`
                : isServerOnline
                  ? 'Gerencie seus roteiros salvos e os disponíveis no servidor'
                  : 'Modo offline: Exibindo apenas roteiros salvos localmente'
          }
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />

        {/* Indicador de Conexão */}
        <ConnectionStatus isOnline={isServerOnline} onCheck={checkServerConnection} />

        {/* Botão de Limpeza de Cache */}
        <StorageCleanupButton onCleanup={cleanupStorage} />

        {/* Botão Envio/Sincronização */}
        <View style={{ paddingRight: 16 }}>
          <TouchableOpacity
            onPress={() => {
              try {
                if (typeof onViewResponses === 'function' && mountedRef.current) {
                  onViewResponses();
                } else {
                  console.error('❌ onViewResponses não é uma função válida');
                  showSnackbar('Erro interno: função de navegação não disponível');
                }
              } catch (error) {
                console.error('❌ Erro ao chamar onViewResponses:', error);
                showSnackbar('Erro ao abrir tela de respostas');
              }
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255,0.1)',
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 20,
            }}
          >
            <Text
              style={{
                color: AVAQualisTheme.colors.onPrimary,
                fontSize: 12,
                marginRight: 6,
              }}
            >
              📁 Pasta de Uploads
            </Text>
            <Icon source="cloud-upload" size={18} color={AVAQualisTheme.colors.onPrimary} />
            <Icon
              source="sync"
              size={16}
              color={AVAQualisTheme.colors.onPrimary}
              style={{ marginLeft: 3 }}
            />
          </TouchableOpacity>
        </View>
      </Appbar.Header>

      {/* Conteúdo Principal */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.scrollContentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[AVAQualisTheme.colors.primary]}
            tintColor={AVAQualisTheme.colors.primary}
          />
        }
      >
        {/* Status da Conexão - Banner quando offline */}
        {!isServerOnline && (
          <Card style={styles.offlineCard}>
            <Card.Content style={styles.offlineCardContent}>
              <Icon source="cloud-off-outline" size={24} color="#F44336" />
              <View style={styles.offlineTextContainer}>
                <Text style={styles.offlineCardTitle}>Modo Offline</Text>
                <Text style={styles.offlineCardMessage}>
                  {serverError
                    ? `Não foi possível conectar ao servidor: ${serverError}`
                    : 'Sem conexão com o servidor. Exibindo roteiros salvos localmente.'}
                </Text>
              </View>
              <Button
                mode="contained"
                onPress={checkServerConnection}
                style={styles.retryButton}
                compact
              >
                Tentar Novamente
              </Button>
            </Card.Content>
          </Card>
        )}

        {/* Indicador de nível de acesso para membros */}
        {effectiveUser?.level === 'membro' && effectiveUser?.id !== 'visitor' && (
          <Card style={styles.infoCard}>
            <Card.Content style={styles.infoCardContent}>
              <Icon source="information" size={20} color="#1976D2" />
              <Text style={styles.infoCardText}>
                Como membro da comissão, você visualiza apenas os roteiros onde foi designado como
                avaliador.
              </Text>
            </Card.Content>
          </Card>
        )}

        {/* ESTADO DE CARREGAMENTO */}
        {loading && allForms.length === 0 && (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <View style={styles.emptyContent}>
                <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
                <Title style={styles.emptyTitle}>Carregando roteiros...</Title>
                <Paragraph style={styles.emptyText}>
                  Aguarde enquanto buscamos os roteiros disponíveis.
                </Paragraph>
              </View>
            </Card.Content>
          </Card>
        )}

        {/* Convite para Login quando for visitante */}
        {!loading && effectiveUser?.id === 'visitor' && <LoginInviteCard onLogin={onOpenLogin} />}

        {/* ESTADO VAZIO - Quando não há formulários */}
        {!loading && allForms.length === 0 && effectiveUser?.id !== 'visitor' && (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <View style={styles.emptyContent}>
                <View style={styles.emptyIconContainer}>
                  <Icon
                    source="file-document-edit"
                    size={48}
                    color={AVAQualisTheme.colors.primary}
                  />
                </View>
                <Title style={styles.emptyTitle}>
                  {effectiveUser?.level === 'membro'
                    ? 'Nenhum roteiro atribuído'
                    : 'Nenhum roteiro disponível'}
                </Title>
                <Paragraph style={styles.emptyText}>
                  {effectiveUser?.level === 'membro'
                    ? 'Você ainda não foi designado como avaliador em nenhum roteiro.'
                    : isServerOnline
                      ? 'Não há roteiros criados localmente ou disponíveis no servidor.'
                      : 'Você está offline e não há roteiros salvos localmente.'}
                  {canCreateForm &&
                    '\n\nPara criar um roteiro personalizado, clique no botão abaixo:'}
                </Paragraph>
                {canCreateForm && onCreateForm && <CreateFormLink onCreateForm={onCreateForm} />}
              </View>
            </Card.Content>
          </Card>
        )}

        {/* SEÇÃO ÚNICA - Com separadores para baixados e disponíveis */}
        {allForms.length > 0 && effectiveUser?.id !== 'visitor' && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Title style={styles.sectionTitle}>
                {effectiveUser?.level === 'membro' ? '📁 Seus Roteiros' : '📁 Roteiros Disponíveis'}
              </Title>
              <Badge style={styles.badge}>{allForms.length}</Badge>
            </View>
            <Text style={styles.sectionSubtitle}>
              {isServerOnline
                ? `${downloadedForms.length} roteiros baixados e ${availableForms.length} disponíveis para download`
                : `${downloadedForms.length} roteiros salvos localmente`}
            </Text>

            {/* SUBSEÇÃO: ROTEIROS BAIXADOS */}
            {downloadedForms.length > 0 && (
              <>
                <View style={styles.subsectionHeader}>
                  <Text style={styles.subsectionTitle}>📥 Roteiros Baixados</Text>
                  <Badge style={styles.subsectionBadge}>{downloadedForms.length}</Badge>
                </View>

                {downloadedForms.map(form => {
                  const complexity = getFormComplexity(form.totalQuestions);
                  const essentialCount = countEssentialRequirements(form);
                  const progressStats = formProgressStats[form.id];
                  const progress = progressStats?.progress?.percentage || 0;
                  const hasValidations =
                    formValidations[form.id] && formValidations[form.id].length > 0;

                  // Determinar estilo do card baseado no progresso
                  const getCardStyle = () => {
                    if (progress === 100 && progressStats?.syncStatus === 'synced') {
                      return styles.completedCard;
                    } else if (progress > 0 && progress < 100) {
                      return styles.priorityCard;
                    } else {
                      return styles.card;
                    }
                  };

                  return (
                    <Card
                      key={form.id}
                      style={[
                        getCardStyle(),
                        styles.downloadedCard,
                        hasValidations && { borderWidth: 2, borderColor: '#4CAF50' },
                      ]}
                    >
                      <Card.Content style={styles.cardContent}>
                        <View style={styles.cardHeader}>
                          <View style={styles.titleSection}>
                            <View style={styles.titleRow}>
                              <View style={styles.titleEditContainer}>
                                <Title
                                  style={
                                    progress === 100 && progressStats?.syncStatus === 'synced'
                                      ? styles.completedCardTitle
                                      : styles.cardTitle
                                  }
                                >
                                  {form.title}
                                </Title>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={16}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() => openEditModal('title', form.id, form.title)}
                                    style={styles.editIconButton}
                                  />
                                )}
                              </View>
                              {/* NOVO: Botão de exclusão permanente para programador/coordenação */}
                              {canEditAvaliadores && (
                                <IconButton
                                  icon="delete-forever"
                                  iconColor="#D32F2F"
                                  size={24}
                                  onPress={() => deleteFormPermanently(form.id, form.title)}
                                  style={styles.permanentDeleteButton}
                                />
                              )}
                            </View>
                            <View style={styles.metaInfo}>
                              <Text style={styles.versionText}>
                                📅 Criado em: {formatDate(form.createdAt)}
                              </Text>
                              {form.synced && form.syncedAt && (
                                <>
                                  <Text style={styles.separator}>•</Text>
                                  <Text style={styles.versionText}>
                                    ☁️ Enviado em: {formatDate(form.syncedAt)}
                                  </Text>
                                </>
                              )}
                              {essentialCount > 0 && (
                                <>
                                  <Text style={styles.separator}>•</Text>
                                  <Text
                                    style={[
                                      styles.complexityText,
                                      { color: '#FFC107', fontWeight: 'bold' },
                                    ]}
                                  >
                                    ⚠️ {essentialCount} RE
                                  </Text>
                                </>
                              )}
                            </View>
                            {/* 
                                <View style={styles.formMetaInfo}>
                                  <Text style={styles.versionText}>📏 v{form.version}</Text>
                                  <Text style={styles.separator}>•</Text>
                                  <Text style={[styles.complexityText, { color: complexity.color }]}>
                                    {complexity.icon} {complexity.label}
                                  </Text>
                                  <Text style={styles.separator}>•</Text>
                                  <Text style={[styles.complexityText, { color: AVAQualisTheme.colors.info }]}>
                                    📋 Personalizado
                                  </Text>
                                </View>
                                */}
                          </View>
                          <View style={styles.statusSection}>
                            <Chip
                              mode="flat"
                              style={styles.downloadedChip}
                              textStyle={styles.chipText}
                              icon="cellphone-check"
                              compact
                            >
                              Baixado
                            </Chip>
                          </View>
                        </View>

                        {/* Indicador de status */}
                        <FormStatusIndicator
                          form={form}
                          formId={form.id}
                          progressStats={progressStats}
                        />

                        <View style={styles.descriptionEditContainer}>
                          <Paragraph style={styles.description} numberOfLines={2}>
                            {form.description}
                          </Paragraph>
                          {canEditAvaliadores && (
                            <IconButton
                              icon="pencil"
                              size={14}
                              iconColor={AVAQualisTheme.colors.primary}
                              onPress={() =>
                                openEditModal('description', form.id, form.description)
                              }
                              style={styles.editIconButtonSmall}
                            />
                          )}
                        </View>
                        {/* Metadados do formulário */}
                        {form.metadata && (
                          <View style={styles.metadataContainer}>
                            {/* Linha 1: Avaliadores com botões de edição */}
                            <View style={styles.metadataRow}>
                              <Text style={styles.metadataText}>👥 Avaliadores:</Text>
                              <View style={styles.avaliadorEditContainer}>
                                <Text style={styles.avaliadorName}>
                                  {getAvaliadorNome(form.metadata.avaliador1)}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={16}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() => {
                                      setEditingFormId(form.id);
                                      setEditingAvaliadorPosition(1);
                                      setAvaliadorSelectorVisible(true);
                                    }}
                                  />
                                )}
                              </View>
                              <Text style={styles.metadataText}> e </Text>
                              <View style={styles.avaliadorEditContainer}>
                                <Text style={styles.avaliadorName}>
                                  {getAvaliadorNome(form.metadata.avaliador2)}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={16}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() => {
                                      setEditingFormId(form.id);
                                      setEditingAvaliadorPosition(2);
                                      setAvaliadorSelectorVisible(true);
                                    }}
                                  />
                                )}
                              </View>
                            </View>

                            {/* Linha 2: Data, Hora, Contato */}
                            <View style={styles.metadataRow}>
                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>📅 Data: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.dataAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal('date', form.id, form.metadata.dataAvaliacao)
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>

                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>⏰ Hora: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.horarioAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal('time', form.id, form.metadata.horarioAvaliacao)
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>

                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>📞 Contato: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.contatoAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal(
                                        'contact',
                                        form.id,
                                        form.metadata.contatoAvaliacao
                                      )
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>
                            </View>

                            {/* Linha 3: Local */}
                            {form.metadata.localAvaliacao && (
                              <View style={styles.metadataRow}>
                                <View style={styles.metadataEditGroup}>
                                  <Text style={styles.metadataText}>📍 Local: </Text>
                                  <Text style={styles.metadataValueText}>
                                    {form.metadata.localAvaliacao}
                                  </Text>
                                  {canEditAvaliadores && (
                                    <IconButton
                                      icon="pencil"
                                      size={14}
                                      iconColor={AVAQualisTheme.colors.primary}
                                      onPress={() =>
                                        openEditModal(
                                          'location',
                                          form.id,
                                          form.metadata.localAvaliacao
                                        )
                                      }
                                      style={styles.metadataEditIcon}
                                    />
                                  )}
                                </View>
                              </View>
                            )}
                            {/* Linha 4: Emails das Lideranças */}
                            {form.metadata.emailsLiderancas &&
                              form.metadata.emailsLiderancas.length > 0 && (
                                <View style={styles.metadataRow}>
                                  <View
                                    style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}
                                  >
                                    <Text style={styles.metadataText}>
                                      📧 Emails: {form.metadata.emailsLiderancas.length} liderança
                                      {form.metadata.emailsLiderancas.length !== 1 ? 's' : ''}
                                    </Text>
                                    {canEditAvaliadores && (
                                      <>
                                        <IconButton
                                          icon="pencil"
                                          size={16}
                                          iconColor={AVAQualisTheme.colors.primary}
                                          onPress={() => {
                                            setEditingFormIdForEmails(form.id);
                                            setEmailsLiderancas(
                                              form.metadata.emailsLiderancas || []
                                            );
                                            setEmailModalVisible(true);
                                          }}
                                        />
                                        <IconButton
                                          icon="email-fast"
                                          size={20}
                                          iconColor={AVAQualisTheme.colors.primary}
                                          onPress={() => openEmailModal(form.id)}
                                          style={{ marginLeft: -8 }}
                                        />
                                      </>
                                    )}
                                  </View>
                                </View>
                              )}

                            {/* Badges de Validação */}
                            {/* Badges de Validação */}
                            {formValidations[form.id] && formValidations[form.id].length > 0 && (
                              <View style={{ marginTop: 8 }}>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    marginBottom: 6,
                                    gap: 8,
                                  }}
                                >
                                  <View
                                    style={{
                                      backgroundColor: '#4CAF50',
                                      borderRadius: 20,
                                      padding: 6,
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      elevation: 2,
                                      shadowColor: '#000',
                                      shadowOffset: { width: 0, height: 1 },
                                      shadowOpacity: 0.2,
                                      shadowRadius: 2,
                                    }}
                                  >
                                    <MaterialCommunityIcons
                                      name="check-decagram"
                                      size={20}
                                      color="white"
                                    />
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 'bold',
                                        color: 'white',
                                        marginLeft: 6,
                                        marginRight: 4,
                                      }}
                                    >
                                      Envio Validado
                                    </Text>
                                  </View>
                                  <Text
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 'bold',
                                      color: '#4CAF50',
                                    }}
                                  >
                                    ({formValidations[form.id].length} validação
                                    {formValidations[form.id].length !== 1 ? 'ões' : ''})
                                  </Text>
                                </View>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                  }}
                                >
                                  {formValidations[form.id].map((validation, index) => (
                                    <View
                                      key={index}
                                      style={{
                                        backgroundColor: '#E8F5E9',
                                        borderRadius: 8,
                                        padding: 8,
                                        borderWidth: 1,
                                        borderColor: '#4CAF50',
                                        minWidth: 140,
                                      }}
                                    >
                                      <View
                                        style={{
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          marginBottom: 2,
                                        }}
                                      >
                                        <MaterialCommunityIcons
                                          name="account-check"
                                          size={16}
                                          color="#4CAF50"
                                        />
                                        <Text
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 'bold',
                                            color: '#2E7D32',
                                            marginLeft: 4,
                                            flex: 1,
                                          }}
                                        >
                                          {validation.avaliadorNome}
                                        </Text>
                                      </View>
                                      <Text
                                        style={{
                                          fontSize: 10,
                                          color: '#2E7D32',
                                          marginLeft: 20,
                                        }}
                                      >
                                        {new Date(validation.dataValidacao).toLocaleDateString(
                                          'pt-BR',
                                          {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          }
                                        )}
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 9,
                                          color: '#558B2F',
                                          fontStyle: 'italic',
                                          marginLeft: 20,
                                          marginTop: 2,
                                        }}
                                      >
                                        Por: {validation.validadoPor}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            )}

                            {/* Indicador de email enviado */}
                            {form.emailsSent && form.emailsSent.length > 0 && (
                              <View
                                style={{
                                  marginTop: 8,
                                  backgroundColor: '#E8F5E9',
                                  borderRadius: 6,
                                  padding: 10,
                                  borderLeftWidth: 3,
                                  borderLeftColor: '#4CAF50',
                                }}
                              >
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    marginBottom: 4,
                                  }}
                                >
                                  <MaterialCommunityIcons
                                    name="check-circle"
                                    size={18}
                                    color="#4CAF50"
                                  />
                                  <Text
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 'bold',
                                      color: '#2E7D32',
                                      marginLeft: 6,
                                    }}
                                  >
                                    ✅ Notificação Enviada
                                  </Text>
                                </View>

                                {(() => {
                                  const lastEmail = form.emailsSent[form.emailsSent.length - 1];
                                  const date = new Date(lastEmail.sentAt);
                                  return (
                                    <View style={{ marginLeft: 24 }}>
                                      <Text style={{ fontSize: 11, color: '#2E7D32' }}>
                                        📅 {date.toLocaleDateString('pt-BR')} às{' '}
                                        {date.toLocaleTimeString('pt-BR', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </Text>
                                      <Text style={{ fontSize: 11, color: '#2E7D32' }}>
                                        👤 Por: {lastEmail.sentBy?.nome || 'Sistema'}
                                      </Text>
                                      {lastEmail.recipients && (
                                        <>
                                          <Text
                                            style={{ fontSize: 11, color: '#2E7D32', marginTop: 2 }}
                                          >
                                            📧 Para: {lastEmail.recipients.to?.join(', ')}
                                          </Text>
                                          <Text style={{ fontSize: 11, color: '#2E7D32' }}>
                                            📋 CC: Avaliadores em cópia
                                          </Text>
                                        </>
                                      )}
                                      {form.emailsSent.length > 1 && (
                                        <Text
                                          style={{
                                            fontSize: 10,
                                            color: '#666',
                                            fontStyle: 'italic',
                                            marginTop: 4,
                                          }}
                                        >
                                          Total de {form.emailsSent.length} envios realizados
                                        </Text>
                                      )}
                                    </View>
                                  );
                                })()}
                              </View>
                            )}

                            {/* Linha de Processos Avaliados */}
                            {/* Linha de Processos Avaliados */}
                            {/* Linha de Processos Avaliados */}
                            {form.metadata.processosAvaliados &&
                              form.metadata.processosAvaliados.length > 0 && (
                                <View style={styles.metadataRow}>
                                  <View style={styles.processosHeaderRow}>
                                    <Text style={styles.metadataText}>
                                      ⚙️ Processos Avaliados:{' '}
                                      {form.metadata.processosAvaliados.length} processo
                                      {form.metadata.processosAvaliados.length !== 1 ? 's' : ''}
                                    </Text>
                                    {canEditAvaliadores && (
                                      <IconButton
                                        icon="pencil"
                                        size={16}
                                        iconColor={AVAQualisTheme.colors.primary}
                                        onPress={() => openProcessoEditModal(form.id)}
                                        style={styles.processosEditIcon}
                                      />
                                    )}
                                  </View>
                                </View>
                              )}
                            {/* Detalhes dos processos */}
                            {form.metadata.processosAvaliados &&
                              form.metadata.processosAvaliados.length > 0 && (
                                <View
                                  style={{
                                    marginTop: 4,
                                    paddingLeft: 20,
                                    paddingRight: 8,
                                    paddingVertical: 4,
                                    backgroundColor: 'rgba(103, 58, 183, 0.05)',
                                    borderRadius: 4,
                                    borderLeftWidth: 2,
                                    borderLeftColor: '#673AB7',
                                  }}
                                >
                                  {form.metadata.processosAvaliados.map((processo, index) => (
                                    <Text
                                      key={processo.id}
                                      style={{
                                        fontSize: 11,
                                        color: '#673AB7',
                                        marginBottom:
                                          index < form.metadata.processosAvaliados.length - 1
                                            ? 2
                                            : 0,
                                        fontWeight: '500',
                                      }}
                                    >
                                      <Text>
                                        {index + 1}. {processo.nome} ({processo.id})
                                      </Text>
                                    </Text>
                                  ))}
                                </View>
                              )}

                            {/* Unidades organizacionais */}
                            {form.metadata.unidadesOrganizacionais &&
                            form.metadata.unidadesOrganizacionais.length > 0 ? (
                              <View style={styles.unidadesExpandable}>
                                <Text style={styles.unidadesLabel}>
                                  <View style={styles.unidadesHeaderRow}>
                                    <Text style={styles.unidadesLabel}>
                                      🏢 Unidades Organizacionais (
                                      {form.metadata.unidadesOrganizacionais.length}):
                                    </Text>
                                    {canEditAvaliadores && (
                                      <IconButton
                                        icon="pencil"
                                        size={16}
                                        iconColor={AVAQualisTheme.colors.primary}
                                        onPress={() => openUnidadeEditModal(form.id)}
                                        style={styles.unidadesEditIcon}
                                      />
                                    )}
                                  </View>
                                </Text>
                                <View style={styles.unidadesInlineContainer}>
                                  {form.metadata.unidadesOrganizacionais.map((unidade, index) => (
                                    <View
                                      key={unidade.id || index}
                                      style={styles.unidadeCardInline}
                                    >
                                      <View style={styles.unidadeCardInlineHeader}>
                                        <Text style={styles.unidadeCardInlineIndex}>
                                          {index + 1}.
                                        </Text>
                                        <Text style={styles.unidadeCardInlineCode}>
                                          {unidade.codigo}
                                        </Text>
                                      </View>
                                      <Text style={styles.unidadeCardInlineName}>
                                        {unidade.nome}
                                      </Text>
                                      <Text style={styles.unidadeCardInlineContact}>
                                        📞 {unidade.telefone} • 📧 {unidade.email}
                                      </Text>
                                      <Text style={styles.unidadeCardInlineHierarchy}>
                                        🏗️ {getUnidadeHierarchy(unidade.id)}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            ) : form.metadata.unidadeOrganizacional ? (
                              <View style={styles.unidadesExpandable}>
                                <Text style={styles.unidadesLabel}>🏢 Unidade Organizacional:</Text>
                                <View style={styles.unidadesInlineContainer}>
                                  <View style={styles.unidadeCardInline}>
                                    <View style={styles.unidadeCardInlineHeader}>
                                      <Text style={styles.unidadeCardInlineCode}>
                                        {form.metadata.unidadeOrganizacional.codigo}
                                      </Text>
                                    </View>
                                    <Text style={styles.unidadeCardInlineName}>
                                      {form.metadata.unidadeOrganizacional.nome}
                                    </Text>
                                    <Text style={styles.unidadeCardInlineContact}>
                                      📞 {form.metadata.unidadeOrganizacional.telefone} • 📧{' '}
                                      {form.metadata.unidadeOrganizacional.email}
                                    </Text>
                                    <Text style={styles.unidadeCardInlineHierarchy}>
                                      🏗️{' '}
                                      {getUnidadeHierarchy(form.metadata.unidadeOrganizacional.id)}
                                    </Text>
                                  </View>
                                </View>
                              </View>
                            ) : null}
                          </View>
                        )}

                        {/* MUDANÇA 8: FormProgressIndicator modificado para sempre aparecer quando há progressStats */}
                        {progressStats && (
                          <FormProgressIndicator form={form} progressStats={progressStats} />
                        )}

                        <Divider style={styles.divider} />

                        <View style={styles.cardFooter}>
                          <View style={styles.questionInfo}>
                            <View style={styles.questionInfoRow}>
                              <Text style={styles.questionsCount}>
                                📝 {form.totalQuestions}{' '}
                                {form.totalQuestions === 1 ? 'requisito' : 'requisitos'}
                              </Text>
                              <Text style={styles.estimatedTime}>⏱️ ~{form.estimatedTime} min</Text>
                              {essentialCount > 0 && (
                                <Text
                                  style={[
                                    styles.estimatedTime,
                                    { color: '#FFC107', fontWeight: 'bold' },
                                  ]}
                                >
                                  ⚠️ {essentialCount} Requisito(s) Essencial(is)
                                </Text>
                              )}
                            </View>
                          </View>
                          {getActionButton(form, progressStats)}
                        </View>
                      </Card.Content>
                    </Card>
                  );
                })}
              </>
            )}

            {/* SUBSEÇÃO: ROTEIROS DISPONÍVEIS */}
            {isServerOnline && availableForms.length > 0 && (
              <>
                <View style={styles.subsectionHeader}>
                  <Text style={styles.subsectionTitle}>☁️ Roteiros Disponíveis no Servidor</Text>
                  <Badge style={styles.subsectionBadge}>{availableForms.length}</Badge>
                </View>

                {availableForms.map(form => {
                  const complexity = getFormComplexity(form.totalQuestions);
                  const essentialCount = countEssentialRequirements(form);

                  return (
                    <Card key={form.id} style={[styles.card, styles.serverCard]}>
                      <Card.Content style={styles.cardContent}>
                        <View style={styles.cardHeader}>
                          <View style={styles.titleSection}>
                            <View style={styles.titleRow}>
                              <Title style={styles.cardTitle}>{form.title}</Title>
                              {/* NOVO: Botão de exclusão permanente para programador/coordenação */}
                              {canEditAvaliadores && (
                                <IconButton
                                  icon="delete-forever"
                                  iconColor="#D32F2F"
                                  size={24}
                                  onPress={() => deleteFormPermanently(form.id, form.title)}
                                  style={styles.permanentDeleteButton}
                                />
                              )}
                            </View>
                            <View style={styles.metaInfo}>
                              <Text style={styles.versionText}>
                                📅 Criado em: {formatDate(form.createdAt)}
                              </Text>
                              {form.updatedAt &&
                                new Date(form.updatedAt) > new Date(form.createdAt) && (
                                  <>
                                    <Text style={styles.separator}>•</Text>
                                    <Text style={styles.versionText}>
                                      🔄 Atualizado em: {formatDate(form.updatedAt)}
                                    </Text>
                                  </>
                                )}
                            </View>

                            <View style={styles.formMetaInfo}>
                              <Text style={styles.versionText}>📏 v{form.version}</Text>
                              <Text style={styles.separator}>•</Text>
                              <Text style={[styles.complexityText, { color: complexity.color }]}>
                                {complexity.icon} {complexity.label}
                              </Text>
                              <Text style={styles.separator}>•</Text>
                              <Text
                                style={[
                                  styles.complexityText,
                                  { color: AVAQualisTheme.colors.info },
                                ]}
                              >
                                📋 Personalizado
                              </Text>
                            </View>
                          </View>
                          <View style={styles.statusSection}>
                            <Chip
                              mode="flat"
                              style={styles.serverChip}
                              textStyle={styles.chipText}
                              icon="cloud"
                              compact
                            >
                              Servidor
                            </Chip>
                            <Chip
                              mode="flat"
                              style={styles.availableChip}
                              textStyle={styles.chipText}
                              icon="cloud-download"
                              compact
                            >
                              Disponível
                            </Chip>
                          </View>
                        </View>

                        {/* Indicador de status para formulários do servidor */}
                        <View
                          style={[
                            styles.formStatusIndicatorCompact,
                            { backgroundColor: '#E8F5E9', borderLeftColor: '#2E7D32' },
                          ]}
                        >
                          <View style={styles.statusCompactRow}>
                            <View style={styles.statusIconContainer}>
                              <Text style={styles.statusIcon}>✅</Text>
                            </View>
                            <View style={styles.statusTextContainer}>
                              <Text style={[styles.statusLabelCompact, { color: '#2E7D32' }]}>
                                Disponível para Download
                              </Text>
                              <Text style={[styles.statusMessageCompact, { color: '#2E7D32' }]}>
                                Baixe este formulário para usar.
                              </Text>
                            </View>
                          </View>
                        </View>

                        <View style={styles.descriptionEditContainer}>
                          <Paragraph style={styles.description} numberOfLines={2}>
                            {form.description}
                          </Paragraph>
                          {canEditAvaliadores && (
                            <IconButton
                              icon="pencil"
                              size={14}
                              iconColor={AVAQualisTheme.colors.primary}
                              onPress={() =>
                                openEditModal('description', form.id, form.description)
                              }
                              style={styles.editIconButtonSmall}
                            />
                          )}
                        </View>

                        {/* Metadados similares aos formulários baixados */}
                        {form.metadata && (
                          <View style={styles.metadataContainer}>
                            {/* Linha 1: Avaliadores com botões de edição */}
                            <View style={styles.metadataRow}>
                              <Text style={styles.metadataText}>👥 Avaliadores:</Text>
                              <View style={styles.avaliadorEditContainer}>
                                <Text style={styles.avaliadorName}>
                                  {getAvaliadorNome(form.metadata.avaliador1)}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={16}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() => {
                                      setEditingFormId(form.id);
                                      setEditingAvaliadorPosition(1);
                                      setAvaliadorSelectorVisible(true);
                                    }}
                                  />
                                )}
                              </View>
                              <Text style={styles.metadataText}> e </Text>
                              <View style={styles.avaliadorEditContainer}>
                                <Text style={styles.avaliadorName}>
                                  {getAvaliadorNome(form.metadata.avaliador2)}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={16}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() => {
                                      setEditingFormId(form.id);
                                      setEditingAvaliadorPosition(2);
                                      setAvaliadorSelectorVisible(true);
                                    }}
                                  />
                                )}
                              </View>
                            </View>

                            {/* Linha 2: Data, Hora, Contato */}
                            <View style={styles.metadataRow}>
                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>📅 Data: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.dataAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal('date', form.id, form.metadata.dataAvaliacao)
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>

                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>⏰ Hora: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.horarioAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal('time', form.id, form.metadata.horarioAvaliacao)
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>

                              <View style={styles.metadataEditGroup}>
                                <Text style={styles.metadataText}>📞 Contato: </Text>
                                <Text style={styles.metadataValueText}>
                                  {form.metadata.contatoAvaliacao}
                                </Text>
                                {canEditAvaliadores && (
                                  <IconButton
                                    icon="pencil"
                                    size={14}
                                    iconColor={AVAQualisTheme.colors.primary}
                                    onPress={() =>
                                      openEditModal(
                                        'contact',
                                        form.id,
                                        form.metadata.contatoAvaliacao
                                      )
                                    }
                                    style={styles.metadataEditIcon}
                                  />
                                )}
                              </View>
                            </View>

                            {/* Linha 3: Local */}
                            {form.metadata.localAvaliacao && (
                              <View style={styles.metadataRow}>
                                <View style={styles.metadataEditGroup}>
                                  <Text style={styles.metadataText}>📍 Local: </Text>
                                  <Text style={styles.metadataValueText}>
                                    {form.metadata.localAvaliacao}
                                  </Text>
                                  {canEditAvaliadores && (
                                    <IconButton
                                      icon="pencil"
                                      size={14}
                                      iconColor={AVAQualisTheme.colors.primary}
                                      onPress={() =>
                                        openEditModal(
                                          'location',
                                          form.id,
                                          form.metadata.localAvaliacao
                                        )
                                      }
                                      style={styles.metadataEditIcon}
                                    />
                                  )}
                                </View>
                              </View>
                            )}
                            {/* Linha de Processos Avaliados */}
                            {/* Processos Avaliados */}

                            {form.metadata.processosAvaliados &&
                              form.metadata.processosAvaliados.length > 0 && (
                                <>
                                  {form.metadata.processosAvaliados.length === 1 ? (
                                    // Exibição inline para 1 processo
                                    <View style={styles.metadataRow}>
                                      <Text style={styles.metadataText}>
                                        ⚙️ Processo: {form.metadata.processosAvaliados[0].nome} (
                                        {form.metadata.processosAvaliados[0].id})
                                      </Text>
                                    </View>
                                  ) : (
                                    // Exibição expandida para múltiplos processos
                                    <>
                                      <View style={styles.metadataRow}>
                                        <Text style={styles.metadataText}>
                                          ⚙️ Processos Avaliados:{' '}
                                          {form.metadata.processosAvaliados.length} processos
                                        </Text>
                                      </View>
                                      <View
                                        style={{
                                          marginTop: 4,
                                          paddingLeft: 20,
                                          paddingRight: 8,
                                          paddingVertical: 4,
                                          backgroundColor: 'rgba(103, 58, 183, 0.05)',
                                          borderRadius: 4,
                                          borderLeftWidth: 2,
                                          borderLeftColor: '#673AB7',
                                        }}
                                      >
                                        {form.metadata.processosAvaliados.map((processo, index) => (
                                          <Text
                                            key={processo.id}
                                            style={{
                                              fontSize: 11,
                                              color: '#673AB7',
                                              marginBottom:
                                                index < form.metadata.processosAvaliados.length - 1
                                                  ? 2
                                                  : 0,
                                              fontWeight: '500',
                                            }}
                                          >
                                            <Text>
                                              {index + 1}. {processo.nome} ({processo.id})
                                            </Text>
                                          </Text>
                                        ))}
                                      </View>
                                    </>
                                  )}
                                </>
                              )}

                            {/* Badges de Validação */}
                            {/* Botão para recarregar validações se estiver online */}
                            {isServerOnline && (
                              <TouchableOpacity
                                onPress={() => forceReloadFormValidations(form.id)}
                                style={{
                                  position: 'absolute',
                                  top: -5,
                                  right: 5,
                                  zIndex: 10,
                                  padding: 5,
                                }}
                              >
                                <MaterialCommunityIcons name="refresh" size={20} color="#4CAF50" />
                              </TouchableOpacity>
                            )}
                            {formValidations[form.id] && formValidations[form.id].length > 0 && (
                              <View style={{ marginTop: 8 }}>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    color: '#4CAF50',
                                    marginBottom: 6,
                                  }}
                                >
                                  ✅ Validações Realizadas:
                                </Text>
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                  }}
                                >
                                  {formValidations[form.id].map((validation, index) => (
                                    <View
                                      key={index}
                                      style={{
                                        backgroundColor: '#E8F5E9',
                                        borderRadius: 8,
                                        padding: 8,
                                        borderWidth: 1,
                                        borderColor: '#4CAF50',
                                        minWidth: 140,
                                      }}
                                    >
                                      <View
                                        style={{
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                          marginBottom: 2,
                                        }}
                                      >
                                        <MaterialCommunityIcons
                                          name="check-decagram"
                                          size={16}
                                          color="#4CAF50"
                                        />
                                        <Text
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 'bold',
                                            color: '#2E7D32',
                                            marginLeft: 4,
                                            flex: 1,
                                          }}
                                        >
                                          {validation.avaliadorNome}
                                        </Text>
                                      </View>
                                      <Text
                                        style={{
                                          fontSize: 10,
                                          color: '#2E7D32',
                                          marginLeft: 20,
                                        }}
                                      >
                                        {new Date(validation.dataValidacao).toLocaleDateString(
                                          'pt-BR',
                                          {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          }
                                        )}
                                      </Text>
                                      <Text
                                        style={{
                                          fontSize: 9,
                                          color: '#558B2F',
                                          fontStyle: 'italic',
                                          marginLeft: 20,
                                          marginTop: 2,
                                        }}
                                      >
                                        Por: {validation.validadoPor}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            )}
                          </View>
                        )}

                        <Divider style={styles.divider} />

                        <View style={styles.cardFooter}>
                          <View style={styles.questionInfo}>
                            <View style={styles.questionInfoRow}>
                              <Text style={styles.questionsCount}>
                                📝 {form.totalQuestions}{' '}
                                {form.totalQuestions === 1 ? 'requisito' : 'requisitos'}
                              </Text>
                              <Text style={styles.estimatedTime}>⏱️ ~{form.estimatedTime} min</Text>
                              {essentialCount > 0 && (
                                <Text
                                  style={[
                                    styles.estimatedTime,
                                    { color: '#FFC107', fontWeight: 'bold' },
                                  ]}
                                >
                                  ⚠️ {essentialCount} Requisito(s) Essencial(is)
                                </Text>
                              )}
                            </View>
                          </View>
                          <Button
                            mode="contained"
                            onPress={() => downloadFormFromServer(form)}
                            icon="download"
                            style={styles.downloadButton}
                            contentStyle={styles.buttonContent}
                            labelStyle={styles.buttonLabel}
                            buttonColor={AVAQualisTheme.colors.info}
                            loading={downloadingFormId === form.id}
                            disabled={downloadingFormId === form.id}
                          >
                            Baixar
                          </Button>
                        </View>
                      </Card.Content>
                    </Card>
                  );
                })}
              </>
            )}
          </View>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Loading Overlay */}
      <LoadingOverlay
        visible={loading}
        title="⏳ Carregando roteiros..."
        subtitle="Preparando seus roteiros"
      />

      {/* Seletor de Avaliador */}
      {avaliadorSelectorVisible && (
        <AvaliadorSelector
          visible={avaliadorSelectorVisible}
          onDismiss={() => {
            setAvaliadorSelectorVisible(false);
            setEditingFormId(null);
            setEditingAvaliadorPosition(null);
          }}
          onSelect={avaliador => {
            if (editingFormId && editingAvaliadorPosition) {
              handleEditAvaliador(editingFormId, editingAvaliadorPosition, avaliador);
            }
          }}
          currentAvaliadorId={
            editingAvaliadorPosition === 1
              ? allForms.find(f => f.id === editingFormId)?.metadata?.avaliador1
              : allForms.find(f => f.id === editingFormId)?.metadata?.avaliador2
          }
          excludeAvaliadorId={
            editingAvaliadorPosition === 1
              ? allForms.find(f => f.id === editingFormId)?.metadata?.avaliador2
              : allForms.find(f => f.id === editingFormId)?.metadata?.avaliador1
          }
        />
      )}

      {/* Editor de Emails das Lideranças */}
      {emailModalVisible && (
        <>
          <EmailEditor
            visible={emailModalVisible}
            onDismiss={() => {
              setEmailModalVisible(false);
              setEditingFormIdForEmails(null);
              setEmailsLiderancas([]);
            }}
            formId={editingFormIdForEmails}
            currentEmails={emailsLiderancas}
            onSave={newEmails => {
              if (editingFormIdForEmails) {
                handleEditEmails(editingFormIdForEmails, newEmails);
              }
            }}
          />

          {/* Modal de Envio de Email para Lideranças */}
          <EmailLeadershipModal
            visible={emailModalVisible}
            onDismiss={() => {
              setEmailModalVisible(false);
              setEmailModalFormId(null);
            }}
            form={allForms.find(f => f.id === emailModalFormId)}
            currentUser={effectiveUser}
            onSendEmail={handleSendLeadershipEmail}
          />
        </>
      )}

      {/* Modal de Edição Genérico */}
      <EditFieldModal
        visible={editModalVisible}
        onDismiss={() => {
          setEditModalVisible(false);
          setEditModalError('');
        }}
        type={editModalType}
        value={editModalValue}
        onChange={setEditModalValue}
        onSave={handleSaveEdit}
        error={editModalError}
        loading={loading} // <-- ADICIONE ESTA LINHA
      />

      {/* Modal de Edição de Unidades Organizacionais */}
      {unidadeEditModalVisible && (
        <Modal
          visible={unidadeEditModalVisible}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setUnidadeEditModalVisible(false)}
        >
          <KeyboardAvoidingView
            style={styles.modalOverlay}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          >
            <TouchableOpacity
              style={StyleSheet.absoluteFillObject}
              activeOpacity={1}
              onPress={() => setUnidadeEditModalVisible(false)}
            />

            <View style={styles.unidadeEditModalContainer}>
              <View style={styles.unidadeEditModalHeader}>
                <Text style={styles.unidadeEditModalTitle}>🏢 Editar Unidades Organizacionais</Text>
                <IconButton
                  icon="close"
                  size={24}
                  onPress={() => setUnidadeEditModalVisible(false)}
                />
              </View>

              <ScrollView style={styles.unidadeEditModalContent}>
                <View style={styles.unidadeEditSearchContainer}>
                  <TextInput
                    value={unidadeEditSearchText}
                    onChangeText={setUnidadeEditSearchText}
                    placeholder="Buscar unidade para adicionar..."
                    placeholderTextColor="#999"
                    style={styles.unidadeEditSearchInput}
                  />

                  {unidadeEditShowDropdown && (
                    <View style={styles.unidadeEditDropdown}>
                      <ScrollView nestedScrollEnabled maxHeight={200}>
                        {unidadeEditFilteredUnidades.map(unidade => (
                          <TouchableOpacity
                            key={unidade.id}
                            style={styles.unidadeEditDropdownItem}
                            onPress={() => {
                              setUnidadeEditSelectedUnidades(prev => [...prev, unidade]);
                              setUnidadeEditSearchText('');
                              setUnidadeEditShowDropdown(false);
                            }}
                          >
                            <Text style={styles.unidadeEditDropdownText}>
                              {unidade.codigo} - {unidade.nome}
                            </Text>
                            <Text style={styles.unidadeEditDropdownSubtext}>
                              📞{unidade.telefone} • 📧{unidade.email}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </View>

                <Text style={styles.unidadeEditSelectedTitle}>
                  Unidades selecionadas ({unidadeEditSelectedUnidades.length}):
                </Text>

                {unidadeEditSelectedUnidades.map((unidade, index) => (
                  <View key={unidade.id} style={styles.unidadeEditSelectedCard}>
                    <View style={styles.unidadeEditSelectedInfo}>
                      <Text style={styles.unidadeEditSelectedCode}>
                        <Text>
                          {index + 1}. {unidade.codigo}
                        </Text>
                      </Text>
                      <Text style={styles.unidadeEditSelectedName}>{unidade.nome}</Text>
                      <Text style={styles.unidadeEditSelectedContact}>
                        📞 {unidade.telefone} • 📧 {unidade.email}
                      </Text>
                    </View>
                    <IconButton
                      icon="close"
                      size={20}
                      iconColor="#F44336"
                      onPress={() => {
                        setUnidadeEditSelectedUnidades(prev =>
                          prev.filter(u => u.id !== unidade.id)
                        );
                      }}
                    />
                  </View>
                ))}
              </ScrollView>

              <View style={styles.unidadeEditModalFooter}>
                <Button mode="text" onPress={() => setUnidadeEditModalVisible(false)}>
                  Cancelar
                </Button>
                <Button
                  mode="contained"
                  onPress={handleSaveUnidadesEdit}
                  loading={loading}
                  disabled={unidadeEditSelectedUnidades.length === 0}
                >
                  Salvar Alterações
                </Button>
              </View>
            </View>
          </KeyboardAvoidingView>
        </Modal>
      )}

      {/* Modal de Edição de Processos Avaliados */}
      {processoEditModalVisible && (
        <Modal
          visible={processoEditModalVisible}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setProcessoEditModalVisible(false)}
        >
          <KeyboardAvoidingView
            style={styles.modalOverlay}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          >
            <TouchableOpacity
              style={StyleSheet.absoluteFillObject}
              activeOpacity={1}
              onPress={() => setProcessoEditModalVisible(false)}
            />

            <View style={styles.processoEditModalContainer}>
              <View style={styles.processoEditModalHeader}>
                <Text style={styles.processoEditModalTitle}>⚙️ Editar Processos Avaliados</Text>
                <IconButton
                  icon="close"
                  size={24}
                  onPress={() => setProcessoEditModalVisible(false)}
                />
              </View>

              <ScrollView style={styles.processoEditModalContent}>
                <View style={styles.processoEditSearchContainer}>
                  <TextInput
                    value={processoEditSearchText}
                    onChangeText={setProcessoEditSearchText}
                    placeholder="Buscar ou criar novo processo..."
                    placeholderTextColor="#999"
                    style={styles.processoEditSearchInput}
                  />

                  {processoEditShowDropdown && (
                    <View style={styles.processoEditDropdown}>
                      <ScrollView nestedScrollEnabled maxHeight={200}>
                        {processoEditFilteredProcessos.length > 0 ? (
                          processoEditFilteredProcessos.map(processo => (
                            <TouchableOpacity
                              key={processo.id}
                              style={styles.processoEditDropdownItem}
                              onPress={() => {
                                setProcessoEditSelectedProcessos(prev => [...prev, processo]);
                                setProcessoEditSearchText('');
                                setProcessoEditShowDropdown(false);
                              }}
                            >
                              <Text style={styles.processoEditDropdownText}>{processo.nome}</Text>
                              <Text style={styles.processoEditDropdownId}>{processo.id}</Text>
                            </TouchableOpacity>
                          ))
                        ) : (
                          <View style={styles.processoEditNoResults}>
                            <Text style={styles.processoEditNoResultsText}>
                              Nenhum processo encontrado
                            </Text>
                            <Button
                              mode="contained"
                              onPress={() => {
                                const newId = `ID${String(processoEditAllProcessos.length + 1).padStart(3, '0')}`;
                                const newProcesso = {
                                  id: newId,
                                  nome: processoEditSearchText.trim(),
                                };
                                setProcessoEditSelectedProcessos(prev => [...prev, newProcesso]);
                                setProcessoEditAllProcessos(prev => [...prev, newProcesso]);
                                setProcessoEditSearchText('');
                                setProcessoEditShowDropdown(false);
                              }}
                              disabled={!processoEditSearchText.trim()}
                              compact
                            >
                              Criar novo: "{processoEditSearchText}"
                            </Button>
                          </View>
                        )}
                      </ScrollView>
                    </View>
                  )}
                </View>

                <Text style={styles.processoEditSelectedTitle}>
                  Processos selecionados ({processoEditSelectedProcessos.length}):
                </Text>

                {processoEditSelectedProcessos.map((processo, index) => (
                  <View key={processo.id} style={styles.processoEditSelectedCard}>
                    {processoEditingId === processo.id ? (
                      <View style={styles.processoEditingContainer}>
                        <TextInput
                          value={processoEditingText}
                          onChangeText={setProcessoEditingText}
                          style={styles.processoEditingInput}
                          autoFocus
                        />
                        <View style={styles.processoEditingActions}>
                          <Button
                            mode="text"
                            onPress={() => {
                              setProcessoEditingId(null);
                              setProcessoEditingText('');
                            }}
                            compact
                          >
                            Cancelar
                          </Button>
                          <Button
                            mode="contained"
                            onPress={() => {
                              if (processoEditingText.trim()) {
                                setProcessoEditSelectedProcessos(prev =>
                                  prev.map(p =>
                                    p.id === processo.id
                                      ? { ...p, nome: processoEditingText.trim() }
                                      : p
                                  )
                                );
                                setProcessoEditAllProcessos(prev =>
                                  prev.map(p =>
                                    p.id === processo.id
                                      ? { ...p, nome: processoEditingText.trim() }
                                      : p
                                  )
                                );
                              }
                              setProcessoEditingId(null);
                              setProcessoEditingText('');
                            }}
                            compact
                          >
                            Salvar
                          </Button>
                        </View>
                        <Text style={styles.processoEditWarning}>
                          ⚠️ Ao editar, todos os roteiros com este processo serão atualizados
                        </Text>
                      </View>
                    ) : (
                      <View style={styles.processoEditSelectedInfo}>
                        <View>
                          <Text style={styles.processoEditSelectedName}>
                            {index + 1}. {processo.nome}
                          </Text>
                          <Text style={styles.processoEditSelectedId}>{processo.id}</Text>
                        </View>
                        <View style={styles.processoEditSelectedActions}>
                          <IconButton
                            icon="pencil"
                            size={18}
                            iconColor={AVAQualisTheme.colors.primary}
                            onPress={() => {
                              setProcessoEditingId(processo.id);
                              setProcessoEditingText(processo.nome);
                            }}
                          />
                          <IconButton
                            icon="close"
                            size={20}
                            iconColor="#F44336"
                            onPress={() => {
                              setProcessoEditSelectedProcessos(prev =>
                                prev.filter(p => p.id !== processo.id)
                              );
                            }}
                          />
                        </View>
                      </View>
                    )}
                  </View>
                ))}
              </ScrollView>

              <View style={styles.processoEditModalFooter}>
                <Button mode="text" onPress={() => setProcessoEditModalVisible(false)}>
                  Cancelar
                </Button>
                <Button
                  mode="contained"
                  onPress={handleSaveProcessosEdit}
                  loading={loading}
                  disabled={processoEditSelectedProcessos.length === 0}
                >
                  Salvar Alterações
                </Button>
              </View>
            </View>
          </KeyboardAvoidingView>
        </Modal>
      )}

      {/* Custom Dialog Component */}
      <CustomDialog
        visible={dialogVisible}
        onDismiss={() => setDialogVisible(false)}
        title={dialogContent.title}
        message={dialogContent.message}
        actions={dialogContent.actions}
      />

      {/* Snackbar para mensagens */}
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{
          label: 'OK',
          onPress: () => setSnackbarVisible(false),
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
}
